import {
  BIG_INITIALS_PATTERN,
  HCP_REGISTRIES,
  registryForCountry,
  isWellFormedRegistryIdentifier,
  normalizeRegistryIdentifier,
} from "../../../../api/src/shared/domain/hcp-registry";
import { registryPresentation } from "./registryPresentation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  HCP_PREFILL_BE_FLAG,
  HCP_PREFILL_NL_FLAG,
  HCP_PREFILL_PT_FLAG,
  HCP_PREFILL_DK_FLAG,
  HCP_PREFILL_ES_FLAG,
  HCP_PREFILL_US_FLAG,
} from "../../../../api/src/shared/growthbook-flags";
import * as Sentry from "@sentry/react";
import { markOAuthSignupCompleted } from "../../domains/auth/stores/oauthSignupCompletionStore";
import { z } from "zod";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { displayToast } from "../../components/app/AppToast";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getCountriesList } from "../../domains/app/utils/countries";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import {
  otpCredentialStore,
  useOtpCredential,
} from "../../domains/auth/stores/otpCredentialStore";
import { useCheckEmailExistsQuery } from "../../domains/auth/hooks/useCheckEmailExistsQuery";
import { useSignupOnboardingState } from "../../domains/auth/hooks/useSignupOnboardingState";
import { isUserAuthentified } from "../../domains/user/utils/isUserAuthentified";
import {
  auth,
  createAccountForVerifiedEmail,
  signInWithMintedToken,
} from "../../services/firebase";
import {
  OTP_CODE_LENGTH,
  readPendingEmailCode,
  useEmailOtp,
  type EmailOtpCredential,
} from "../../domains/auth/hooks/useEmailOtp";
import {
  apiClient,
  getApiQueryData,
  useApiQuery,
  useApiRevalidate,
  type Api,
} from "../../services/api";
import {
  useCurrentUser,
  reloadCurrentUser,
  type CurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { useLogOutUser } from "../../domains/user/hooks/useLogOutUser";
import { getRoomInvitePathname } from "../../domains/room/utils/getRoomPathname";
import { clearRoomInviteCodeData } from "../../domains/auth/stores/roomInviteCodeStore";
import {
  clearPostLoginRedirectPath,
  getPostLoginRedirectPath,
} from "../../domains/app/utils/postLoginRedirect";
import { resolvePostAuthDestination } from "../../domains/app/utils/postAuthDestination";
import {
  gaEventStartSignUp,
  gaEventViewSignUp,
  gaEventCompleteOnboarding,
} from "../../tools/analytics/domains/auth.analytics";
import {
  gaEventCountrySubmitted,
  gaEventHcpGateAnswered,
  gaEventMainSpecialtySubmitted,
  gaEventPracticeOfferAbandoned,
  gaEventPracticeProfilesConfirmed,
  gaEventPracticeQuestionShown,
  gaEventPracticeQuestionSkipped,
  gaEventPracticeRecapEditClicked,
  gaEventNamesSubmitted,
  gaEventProfessionSubmitted,
  gaEventRegistryLookupResult,
  gaEventRegistryProfileConfirmed,
  gaEventRegistryStepResolvedManually,
  gaEventSignupStepBack,
  gaEventSignupStepViewed,
  type RegistryLookupMode,
  type SignupStepName,
} from "../../tools/analytics/domains/onboarding.analytics";
import { gaEventRoomJoin } from "../../tools/analytics/domains/invite.analytics";
import { claimOncePerSession } from "../../tools/analytics/eventIdentity";
import { HIDDEN_SPECIALTY_IDS } from "../../tools/utils";
import type { ProfessionListState } from "./components/professionPriority";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { OauthRoomsConfig } from "../../features/oauth";
import { useStartRoomOAuth } from "../../features/oauth/useStartRoomOAuth";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import { SoundEffect } from "../../interfaces";
import { iosPlaySound } from "../../tools/ios";
import {
  onboardingFlowStore,
  resetOnboardingFlow,
  updateOnboardingFlow,
  useOnboardingFlowStore,
} from "../../domains/auth/stores/onboardingFlowStore";
import { resetSignupFirstVisitFlags } from "../../domains/user/stores/firstVisitFlagsStore";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";
import { bigSearchInitialFromFirstName } from "../../domains/user/utils/bigSearchInitialFromFirstName";
import type {
  RegistryCandidateView,
  RegistryState,
} from "./steps/RegistryStep";

type RppsLookupBody =
  Api["post"]["/webapp/public/onboarding/rpps-lookup"]["output"]["body"];
type RegistryLookupBody =
  Api["post"]["/webapp/public/onboarding/registry-lookup"]["output"]["body"];
type DirectoryLookupBody =
  Api["post"]["/webapp/public/hcp-directory/search"]["output"]["body"];
type RegistryLookupResult = Exclude<
  RegistryState,
  { status: "loading" | "editing" }
>;

function registryStateFromRegistryLookup(
  body: RegistryLookupBody
): RegistryLookupResult {
  if (body.status === "page") return { ...body, loadState: "idle" };
  return body;
}

function registryStateFromRppsLookup(
  body: RppsLookupBody
): RegistryLookupResult {
  if (body.status === "page")
    return {
      status: "page",
      candidates: body.candidates.map(({ rppsId, ...candidate }) => ({
        ...candidate,
        registry: "rpps",
        identifier: rppsId,
        location: null,
      })),
      nextPage: body.nextPage,
      loadState: "idle",
    };
  if (body.status === "not_found") return body;
  if (body.status === "found") {
    const { rppsId, ...profile } = body.profile;
    return {
      status: "found",
      profile: {
        ...profile,
        registry: "rpps",
        identifier: rppsId,
        location: null,
      },
    };
  }
  return {
    status: "multiple",
    truncated: false,
    candidates: body.candidates.map(({ rppsId, ...candidate }) => ({
      ...candidate,
      registry: "rpps",
      identifier: rppsId,
      location: null,
    })),
  };
}

function registryStateFromDirectoryLookup(
  body: DirectoryLookupBody
): RegistryLookupResult | { status: "unavailable" } {
  if (body.status === "disabled" || body.status === "unavailable")
    return { status: "unavailable" };
  if (body.status === "inactive") return { status: "inactive" };
  if (body.status === "not_found") return { status: "not_found" };
  if (body.status === "too_many_results") return { status: "too_many_results" };
  const toCandidate = (
    profile:
      | Extract<DirectoryLookupBody, { status: "found" }>["profile"]
      | Extract<DirectoryLookupBody, { status: "page" }>["candidates"][number]
  ): RegistryCandidateView => {
    if (profile.registry === "om")
      return {
        ...profile,
        civility: null,
        organizationName: null,
        location: profile.medicalDistrict,
      };
    if (profile.registry === "reps")
      return {
        ...profile,
        civility: null,
        organizationName: null,
      };
    if (profile.registry === "big" || profile.registry === "stps")
      return {
        ...profile,
        civility: null,
        organizationName: null,
        location: null,
      };
    const address = profile.addresses[0];
    return {
      ...profile,
      registry: "inami",
      civility: null,
      organizationName: null,
      location:
        address === undefined
          ? null
          : address.city.fr !== null
            ? address.city.fr
            : address.city.nl,
    };
  };
  if (body.status === "found")
    return { status: "found", profile: toCandidate(body.profile) };
  if (body.status === "page")
    return {
      status: "page",
      candidates: body.candidates.map(toCandidate),
      nextPage: body.nextPage,
      loadState: "idle",
    };
  return {
    status: "multiple",
    candidates: body.candidates.map(toCandidate),
    truncated: false,
  };
}
import {
  toggleDisambiguation,
  canLeavePracticeAxisQuestion,
  canSubmitPracticeProfiles,
  resolvePracticePhase,
  type PracticePickerPhase,
  effectivePracticeDisambiguation,
  inferredPracticeCount,
  practiceAnswerForSpecialty,
  practiceSearchProvenanceAfter,
  practiceSelectionDelta,
  selectedProfileUids,
  showsPracticeRecap,
  togglePracticeProfile,
  type PracticePreSelection,
} from "../../domains/practice/practiceProfileSelection";
import {
  useSignupPracticeArm,
  usePracticeDeclaration,
} from "../../domains/practice/hooks/usePracticeArm";
import { savePracticeProfiles } from "../../domains/practice/savePracticeProfiles";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { useSpecialtySearch } from "../../domains/app/useLabelSearchAssist";
import { usePracticeSearchAssist } from "../../domains/practice/usePracticeSearchAssist";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import {
  askForProfessionalRecord,
  needsProfessionalRecord,
} from "../../components/room/ProfessionalRecordHost";
import { openRoomWelcome } from "../../components/room/RoomWelcomeHost";
import {
  gaEventPartnerObjectionAnswered,
  gaEventPartnerObjectionShown,
} from "../../tools/analytics/domains/partners.analytics";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Fixed carousel slides, in order — also the canonical step order for analytics
 * (`step_index` is the position here). Onboarding is carousel-only, so there is
 * no second, longer list of phases to keep in sync.
 *
 * On mobile every slide is always in the DOM so the translateX math stays fixed;
 * both layouts only *visit* the ones the flow routes through (see
 * `visitsRegistry` for the supported HCP registries).
 *
 * Account creation + profile save is NOT a slide: it runs in place on the last
 * step (`isFinalizing`), which spins its own CTA rather than sliding
 * the user into a screen holding nothing but a loader.
 *
 * Slide indices are named below rather than written as literals: they are read
 * from four files, and inserting a slide silently reassigns every bare number.
 */
const CAROUSEL_STEP_NAMES = [
  "otp",
  "intro",
  "name",
  "country",
  "registry",
  "profession",
  "specialty",
  "practice",
  "partner_objection",
] as const satisfies readonly SignupStepName[];

export const onboardingLocationStateSchema = z.object({
  email: z.string().nullable().default(null),
});

export const CAROUSEL_SLIDES = CAROUSEL_STEP_NAMES.length;

/**
 * Carousel index of the email-code slide — the FIRST thing an email signup
 * answers, before the product has even introduced itself.
 *
 * The address was typed on the surface that sent the user here (the `/welcome`
 * sheet, the `/signup` page), so the code is already in flight when the
 * carousel opens. Everything after this slide therefore runs against a real
 * account, which is the shape an SSO signup has always had.
 *
 * Always in the DOM, visited by a fresh email signup — same shape as the registry
 * and Practice slides, which are also mounted for everyone and walked by some.
 * An already-authenticated arrival (SSO, returning user) has nothing to prove
 * here.
 */
export const OTP_SLIDE = 0;
/** Carousel index of the "Hi, I'm Juisci" intro + healthcare-professional gate. */
export const INTRO_SLIDE = 1;
/** Carousel index of the first/last name slide. */
export const NAME_SLIDE = 2;
/** Carousel index of the country slide. */
export const COUNTRY_SLIDE = 3;
/** Carousel index of the supported HCP registry lookup slide. */
export const REGISTRY_SLIDE = 4;
/** Carousel index of the profession slide (registry results resolve into this). */
export const PROFESSION_SLIDE = 5;
/** Carousel index of the specialty slide. */
export const SPECIALTY_SLIDE = 6;
/**
 * Carousel index of the Practice Profiles slide — visited only when the declared
 * Specialty has a catalogue (practice-profiles ticket 08), which for this lot is
 * the two pilots and nobody else.
 */
export const PRACTICE_SLIDE = 7;
/**
 * The id the footer's Continue submits on the Practice Profiles slide.
 *
 * The CTA lives in the footer band, outside whatever panel is on stage, and it
 * reaches its question through `form=`. So EVERY state of this slide that
 * enables Continue must render a form under this id: the recap does, the
 * catalogue does, and the agent's waiting screen does not because its Continue
 * is disabled. Exported because the id is a contract between three files, and a
 * second literal is how a state ends up with a button that submits nothing.
 */
export const PRACTICE_FORM_ID = "onboarding-form-practice";
/**
 * Carousel index of the partner objection slide (Figma O1,
 * docs/partner-objection.md REQ-01): the last step, after the Practice Profiles
 * question. Its Continue saves the choice, then finalizes the account in
 * place.
 */
export const PARTNER_OBJECTION_SLIDE = 8;
/** The id the footer's Continue submits on the partner objection slide. */
export const PARTNER_OBJECTION_FORM_ID = "onboarding-form-partner-objection";

/**
 * How long Continue may wait on the Practice Profiles offer before giving up on
 * it (practice-profiles ticket 08).
 *
 * The wait buys one thing only: a progress bar that does not gain a segment
 * behind a practitioner who already walked past the slide. That is worth a
 * moment and nothing more — an endpoint that hangs must never be able to hold a
 * signup, which is the one rule this whole feature was given. Past the deadline
 * the flow proceeds as if the Specialty had no catalogue, which is the answer
 * for 93 of the ~95 anyway.
 */
const PRACTICE_OFFER_DEADLINE_MS = 3_000;

/**
 * How long the agent may keep a practitioner on the waiting screen before the
 * list is shown instead (practice-profiles ticket 09).
 *
 * The server's own budget is smaller — a web search plus one model call, each
 * bounded — so reaching this means the response is not coming: a hung
 * connection, a dead tab wakeup, a proxy holding the socket. It sits above the
 * server's worst case (5 s of search plus 12 s of model) rather than inside it:
 * a bound that fires while the answer is still coming would throw away the
 * recognition it was waiting for.
 *
 * It exists at all because "nothing here may stop someone finishing their
 * signup" cannot be a promise the server alone keeps: whatever the server does,
 * the client must be able to leave.
 */
const PRACTICE_INFERENCE_DEADLINE_MS = 20_000;

/**
 * Where the flow stands once the country is answered. One rule, read from two
 * places — the transition itself, and the resume that has to land somewhere
 * sensible after a reload — so the two cannot disagree.
 *
 * The registry slide needs the healthcare-professional certification, given on
 * the intro or in the desktop gate's legal line.
 */
function slideAfterCountry(args: {
  country: string;
  isHealthcareProfessional: true | null;
  npiPrefillEnabled: boolean;
  bePrefillEnabled: boolean;
  nlPrefillEnabled: boolean;
  ptPrefillEnabled: boolean;
  dkPrefillEnabled: boolean;
  esPrefillEnabled: boolean;
}): number {
  const registry = registryForCountry(args.country);
  return (registry === "rpps" ||
    (registry === "npi" && args.npiPrefillEnabled) ||
    (registry === "inami" && args.bePrefillEnabled) ||
    (registry === "big" && args.nlPrefillEnabled) ||
    (registry === "om" && args.ptPrefillEnabled) ||
    (registry === "stps" && args.dkPrefillEnabled) ||
    (args.country === "ES" && args.esPrefillEnabled)) &&
    args.isHealthcareProfessional === true
    ? REGISTRY_SLIDE
    : PROFESSION_SLIDE;
}

// ---------------------------------------------------------------------------
// Flow controller
// ---------------------------------------------------------------------------

/**
 * Whole onboarding flow logic (state machine, analytics, registry lookup, account
 * creation + finalize, room join) shared by the mobile carousel layout and the
 * desktop layout. Layout concerns (carousel, overlaid bands, keyboard handling)
 * stay in the page components; everything else lives here so the two platforms
 * can never drift apart.
 *
 * The flow is the carousel and nothing else: it ends on its last question,
 * whose Continue finalizes the account and exits. Feed personalization (topics,
 * push opt-in) is NOT part of signup — it lives on the Explore gate
 * (`/onboarding/interests`, `InterestsProfilingPage`), which every new user
 * walks the first time they open Explore because onboarding declares no
 * interest on their behalf.
 */
export function useOnboardingFlow() {
  const history = useHistory();
  const location = useLocation();
  const { email: stateEmail } = useLocationState(onboardingLocationStateSchema);
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const npiPrefillEnabled = useFeatureIsOn(HCP_PREFILL_US_FLAG);
  const bePrefillEnabled = useFeatureIsOn(HCP_PREFILL_BE_FLAG);
  const nlPrefillEnabled = useFeatureIsOn(HCP_PREFILL_NL_FLAG);
  const dkPrefillEnabled = useFeatureIsOn(HCP_PREFILL_DK_FLAG);
  const ptPrefillEnabled = useFeatureIsOn(HCP_PREFILL_PT_FLAG);
  const esPrefillEnabled = useFeatureIsOn(HCP_PREFILL_ES_FLAG);
  const otp = useEmailOtp({ intent: "signup", surface: "signup_page" });

  const {
    onboardingRoomInviteCode: roomInviteCode,
    onboardingRoomUtmSource,
    onboardingRoomUtmMedium,
    onboardingRoomUtmContent,
    onboardingRoomDiscoveryChannel,
    onboardingRoomPlaylistId,
    onboardingRoomData: invitedRoom,
    isInvitedToRoom,
    clearSignupOnboardingState,
  } = useSignupOnboardingState();
  const {
    currentUser,
    isLoading: isCurrentUserLoading,
    isAuthenticated,
  } = useCurrentUser();
  const logOutUser = useLogOutUser();
  const revalidate = useApiRevalidate();

  // The persisted onboarding-flow fields live in the session store so the flow
  // survives a reload or a killed app mid-signup, and preserves user input when
  // navigating back/forward between steps.
  const [flow] = useOnboardingFlowStore();
  /**
   * The address this arrival names: the surface that sent the user here or,
   * when none did and the store is empty, the signup code this device had
   * mailed. The second is the reader coming back from their mailbox to an app
   * the OS reloaded or killed meanwhile, which took the router state and the
   * session-backed store with it.
   */
  const [arrivalEmail] = useState(() => {
    if (stateEmail !== null) return stateEmail;
    if (flow.email !== "") return null;
    const pending = readPendingEmailCode();
    return pending !== null && pending.intent === "signup"
      ? pending.email
      : null;
  });
  const registry =
    flow.country === "ES" && esPrefillEnabled
      ? ("reps" as const)
      : registryForCountry(flow.country);
  const {
    accountCreated,
    firstName,
    lastName,
    email,
    country,
    profession: storedProfession,
    specialty: storedSpecialty,
    registryProfession,
    registryIdentity,
    hcpDirectorySelection,
    registrySpecialty,
    registryProfileMode,
    registryVerified: storedRegistryVerified,
    employer: storedEmployer,
    isHealthcareProfessional,
  } = flow;
  const hiddenBeProfile =
    !bePrefillEnabled &&
    ((hcpDirectorySelection !== null &&
      hcpDirectorySelection.registry === "health_pro") ||
      (registryIdentity !== null && registryIdentity.registry === "inami"));
  const hiddenBigProfile =
    !nlPrefillEnabled &&
    ((hcpDirectorySelection !== null &&
      hcpDirectorySelection.registry === "big") ||
      (registryIdentity !== null && registryIdentity.registry === "big"));
  const hiddenStpsProfile =
    !dkPrefillEnabled &&
    ((hcpDirectorySelection !== null &&
      hcpDirectorySelection.registry === "stps") ||
      (registryIdentity !== null && registryIdentity.registry === "stps"));
  const hiddenOmProfile =
    !ptPrefillEnabled &&
    ((hcpDirectorySelection !== null &&
      hcpDirectorySelection.registry === "om") ||
      (registryIdentity !== null && registryIdentity.registry === "om"));
  const hiddenRepsProfile =
    !esPrefillEnabled &&
    hcpDirectorySelection !== null &&
    hcpDirectorySelection.registry === "reps";
  const availableDirectorySelection =
    hcpDirectorySelection !== null &&
    ((hcpDirectorySelection.registry === "stps" &&
      country === "DK" &&
      dkPrefillEnabled) ||
      (hcpDirectorySelection.registry === "health_pro" &&
        country === "BE" &&
        bePrefillEnabled) ||
      (hcpDirectorySelection.registry === "big" &&
        country === "NL" &&
        nlPrefillEnabled) ||
      (hcpDirectorySelection.registry === "om" &&
        country === "PT" &&
        ptPrefillEnabled) ||
      (hcpDirectorySelection.registry === "reps" &&
        country === "ES" &&
        esPrefillEnabled))
      ? hcpDirectorySelection
      : null;
  const availableRegistryIdentity =
    registryIdentity !== null &&
    ((registryIdentity.registry === "npi" && !npiPrefillEnabled) ||
      (registryIdentity.registry === "inami" && !bePrefillEnabled) ||
      (registryIdentity.registry === "big" && !nlPrefillEnabled) ||
      (registryIdentity.registry === "om" && !ptPrefillEnabled) ||
      (registryIdentity.registry === "stps" && !dkPrefillEnabled))
      ? null
      : registryIdentity;
  const registryPresentationSource =
    availableDirectorySelection !== null
      ? availableDirectorySelection.registry === "health_pro"
        ? "inami"
        : availableDirectorySelection.registry
      : availableRegistryIdentity === null
        ? null
        : availableRegistryIdentity.registry;
  const hiddenNpiProfile =
    registryIdentity !== null &&
    registryIdentity.registry === "npi" &&
    !npiPrefillEnabled;
  const selectedProfession =
    (hiddenNpiProfile ||
      hiddenBeProfile ||
      hiddenBigProfile ||
      hiddenOmProfile ||
      hiddenStpsProfile ||
      hiddenRepsProfile) &&
    registryProfession.length > 0 &&
    storedProfession === registryProfession
      ? null
      : storedProfession;
  const selectedSpecialty =
    (hiddenNpiProfile ||
      hiddenBeProfile ||
      hiddenBigProfile ||
      hiddenOmProfile ||
      hiddenStpsProfile ||
      hiddenRepsProfile) &&
    registrySpecialty.length > 0 &&
    storedSpecialty === registrySpecialty
      ? ""
      : storedSpecialty;
  const employer =
    hiddenNpiProfile ||
    hiddenBeProfile ||
    hiddenBigProfile ||
    hiddenOmProfile ||
    hiddenStpsProfile ||
    hiddenRepsProfile
      ? ""
      : storedEmployer;
  const registryVerified =
    hiddenNpiProfile ||
    hiddenBeProfile ||
    hiddenBigProfile ||
    hiddenOmProfile ||
    hiddenStpsProfile ||
    hiddenRepsProfile
      ? false
      : storedRegistryVerified;
  const registryFound =
    registryVerified || availableDirectorySelection !== null;
  const registryFoundSource = registryFound ? registryPresentationSource : null;
  if (registryFound && registryFoundSource === null)
    throw new Error("Confirmed registry profile has no source");
  const practice =
    (hiddenNpiProfile ||
      hiddenBeProfile ||
      hiddenBigProfile ||
      hiddenOmProfile ||
      hiddenStpsProfile) &&
    registrySpecialty.length > 0 &&
    flow.practice !== null &&
    flow.practice.specialtyUid === registrySpecialty
      ? null
      : flow.practice;

  useEffect(() => {
    if (
      !hiddenNpiProfile &&
      !hiddenBeProfile &&
      !hiddenBigProfile &&
      !hiddenOmProfile &&
      !hiddenStpsProfile
    )
      return;
    discardRegistrySelection(null);
  }, [
    hiddenNpiProfile,
    hiddenBeProfile,
    hiddenBigProfile,
    hiddenOmProfile,
    hiddenStpsProfile,
    selectedProfession,
    selectedSpecialty,
    employer,
    practice,
  ]);

  const [isLoading, setLoading] = useState(true);
  const [hasSignupStarted, setSignupStarted] = useState(false);
  const accountReadyRef = useRef<Promise<void>>(Promise.resolve());
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);
  const [isReturningUser, setIsReturningUser] = useState(false);
  // Step state — if the account is already created (a reload after signup
  // remounts this component), resume on the last question
  // with its finalize already running. Read the store synchronously for the
  // initial values only; ongoing changes come from `flow`.
  // Where the carousel opens, and where a remount lands.
  //
  // A reload or a killed app remounts this component at any slide. The sign-in
  // on the name slide does not: `SignupOnboardingRoute` keeps the page mounted
  // while that session loads. Keying the resume on `accountCreated` used to be
  // enough because the account was only ever created last; the code creates it
  // before the product has introduced itself, so the resume reads what actually
  // happened instead.
  /**
   * Desktop groups the form questions: the name with the country (Figma
   * 7092-5913), and the profession with the specialty. Those pairs are not
   * slides of their own there. The mobile carousel keeps every question on its
   * own slide: that is what its translateX math is fixed by
   * (`CAROUSEL_SLIDES`), and it has one screen's width to spend per question.
   *
   * ONE flag for both groupings, because they are one decision — the desktop
   * form asks in groups — and two would let the flow end up half-grouped.
   *
   * Read ONCE at mount, not reactively. `isDesktopLayout()` follows the viewport,
   * and a practitioner who resizes across the breakpoint mid-signup must not
   * have the step machine reshuffled under them — the progress bar would gain
   * or lose a segment and Back would point somewhere else. A reload mounts a
   * new flow, which reads it again.
   */
  const [groupsQuestions] = useState(() => isDesktopLayout());
  // The signup does not ask how the practitioner heard of Juisci: JuisciGPT
  // does, after signup (`DesktopAcquisitionCard`, `AcquisitionSuggestionCard`).
  const partnerObjectionAction = useSingleFlightAction();

  /**
   * This run of onboarding was handed over by the desktop auth gate modal: the
   * address is proved, the code is spent, and the healthcare-profession
   * certification was given in the legal line under that card.
   *
   * Read once, like `groupsQuestions`. The modal marks the handover in the
   * flow store, and the handover is alive while its proof is in memory or once
   * the account it created exists. A reload between the code and the name
   * drops the proof with nothing created, so that run restarts on the code
   * slide. It decides which slides this run HAS, so it must not change under
   * the practitioner mid-flow.
   *
   * The handover covers ONE address. A signup sheet that sends another one
   * starts a run of its own, which the arrival effect below resets around it.
   */
  const [entersFromDesktopGate] = useState(
    () =>
      groupsQuestions &&
      flow.entrance === "desktop-gate" &&
      (arrivalEmail === null || arrivalEmail === flow.email) &&
      (otpCredentialStore.get() !== null || flow.accountCreated)
  );

  const [step, setStep] = useState<number>(() =>
    // The links of the partner objection slide leave the carousel, and the
    // account exists by then: come back to the slide, not the country. Its
    // Continue finalizes, so a finalize resumes there too.
    flow.partnerObjectionStage !== "none"
      ? PARTNER_OBJECTION_SLIDE
      : flow.finalizeStarted
        ? // The finalize ran on the last question, and the profession screen is
          // the one every run has.
          PROFESSION_SLIDE
        : // Signed in on the name slide, which is where the account is created —
          // resume on the slide AFTER it, never back onto it. On the grouped form
          // the country rode on that same screen, so "after it" is wherever the
          // country sends the flow; landing on the name again made the
          // practitioner press Continue a second time on a screen they had just
          // filled, and the verification screen behind it flashed past unseen.
          // The RPPS slide is safe to resume onto because the effect below enters
          // the lookup for any arrival that finds no request in flight.
          flow.otpVerified && flow.accountCreated
          ? groupsQuestions
            ? slideAfterCountry({
                country: flow.country,
                isHealthcareProfessional: flow.isHealthcareProfessional,
                npiPrefillEnabled,
                bePrefillEnabled,
                nlPrefillEnabled,
                ptPrefillEnabled,
                dkPrefillEnabled,
                esPrefillEnabled,
              })
            : COUNTRY_SLIDE
          : // Desktop asks for the address AND the code in the gate modal, and
            // certifies the healthcare profession in the legal line under it. So
            // by the time onboarding opens, the first two slides have already
            // happened somewhere else and the first question left is the name.
            entersFromDesktopGate
            ? NAME_SLIDE
            : // A fresh email signup opens on the code slide. So does a reload
              // between the code and the account, because the proof of the
              // verified address lives in memory and a reload has just dropped it.
              !flow.accountCreated
              ? OTP_SLIDE
              : INTRO_SLIDE
  );
  // Account creation + profile save, rendered in place on the last question
  // (spinner in its CTA) rather than as a slide of its own.
  const [isFinalizing, setFinalizing] = useState(() => flow.finalizeStarted);
  const accountCreationAction = useSingleFlightAction();
  const terminalAction = useSingleFlightAction();

  // The digits typed on the OTP slide. Deliberately NOT persisted: a live
  // credential has no business surviving in session storage.
  const [otpCode, setOtpCode] = useState("");
  /**
   * The proof that the address was verified: a Firebase custom token and the uid
   * it opens, held from the code slide until the account can actually be
   * created.
   *
   * In memory ON PURPOSE. It is a bearer credential for that account, and the
   * flow store is `sessionStorage`. The cost is that a reload before the account
   * exists loses it — handled by resuming on the code slide (mobile) or by the
   * gate modal reopening (desktop), both of which ask for a fresh code.
   *
   * It lives in a module store rather than this hook's state because on desktop
   * the code is entered in the auth gate modal, on another route: the screen
   * that earns the proof and the screen that spends it are no longer the same
   * component. See `otpCredentialStore` for why it is neither session storage
   * nor router state.
   */
  const [otpCredential, setOtpCredential] = useOtpCredential();
  // List search queries. Live here (not in the steps) because the search input
  // is rendered outside the scrolling list (mobile footer band / desktop footer).
  const [countrySearch, setCountrySearch] = useState("");
  const [professionSearch, setProfessionSearch] = useState("");
  // The profession picker's "Show more": open until the flow leaves the slide.
  const [professionList, setProfessionList] =
    useState<ProfessionListState>("collapsed");
  const [specialtySearch, setSpecialtySearch] = useState("");
  // Which of the practice screen's two questions is showing, and the search text
  // filtering its list. Keyed by the Specialty they belong to rather than reset
  // by an effect: a practitioner who steps back and declares a different
  // Specialty is looking at a different catalogue, with a different axis, so the
  // phase and the search they left behind describe nothing. Same trick as
  // `practiceOfferAbandonedFor` below, and for the same reason.
  const [practiceScreen, setPracticeScreen] = useState<{
    specialty: string;
    phase: PracticePickerPhase;
    search: string;
  } | null>(null);
  // Which Specialty's CATALOGUE has actually been on screen. Separate from
  // `practiceScreen` because Back erases that one to bring the recap back, and
  // the one thing this feature has to measure is whether the list was opened at
  // all (`confirmed_from`) — a question no screen state can answer once the
  // screen it describes has been walked out of. Keyed by Specialty for the same
  // reason as the screen: another catalogue is another question.
  const [practiceCatalogueOpenedFor, setPracticeCatalogueOpenedFor] = useState<
    string | null
  >(null);
  // Which Specialty's slide was left by DECLARING. Not derivable from the stored
  // declaration: a practitioner can declare, walk back, check a row and press
  // Continue, and those are two different answers to `confirmed_from` for the
  // same stored state. Keyed by Specialty, same reason as above.
  const [practiceDeclaredFor, setPracticeDeclaredFor] = useState<string | null>(
    null
  );
  // Which practices were ticked THROUGH THE SEARCH FIELD, and which of those sat
  // on a row only the assisted layer surfaced. Analytics only: the answer itself
  // is the uids, and how a uid was reached is not part of it.
  //
  // Keyed by Specialty like the two above, and for the same reason — another
  // catalogue is another question, and a provenance carried across would credit
  // the field for a practice ticked off a different list. Held here rather than
  // in the flow store because it does not survive a reload and must not: a
  // practitioner who comes back to a restored screen and ticks a row did not
  // reach it by typing.
  const [practiceSearchPicks, setPracticeSearchPicks] = useState<{
    specialty: string;
    viaSearch: ReadonlySet<string>;
    viaAssist: ReadonlySet<string>;
  } | null>(null);

  // Registry lookup. `null` until the country step enters the registry slide
  // and fires a lookup; then loading resolves to a country-specific result.
  const [registryState, setRegistryState] = useState<RegistryState | null>(
    null
  );
  const [pendingRegistryConfirmation, setPendingRegistryConfirmation] =
    useState<{
      candidate: RegistryCandidateView;
      lookupGeneration: number;
    } | null>(null);
  // RPPS id typed on the not-found fallback to resolve the record by id.
  const [stpsName, setStpsName] = useState("");
  const [stpsProfessionCode, setStpsProfessionCode] = useState("");
  const [stpsSearchMode, setStpsSearchMode] = useState<"name" | "identifier">(
    "name"
  );
  const stpsCapabilities = useApiQuery(
    "/webapp/public/hcp-directory",
    registry === "stps" && dkPrefillEnabled ? {} : false
  );
  const stpsDirectory =
    stpsCapabilities.state === "success"
      ? stpsCapabilities.body.directories.find(
          (entry) => entry.registry === "stps"
        )
      : undefined;
  const stpsProfessions =
    stpsDirectory === undefined ? [] : stpsDirectory.professions;
  const [registryId, setRegistryId] = useState("");
  const [registryInitials, setRegistryInitials] = useState("");
  const [repsFirstName, setRepsFirstName] = useState(firstName);
  const [repsFirstSurname, setRepsFirstSurname] = useState(lastName);
  const [repsSecondSurname, setRepsSecondSurname] = useState("");
  const [repsSubmittedQuery, setRepsSubmittedQuery] = useState<{
    firstName: string;
    firstSurname: string;
    secondSurname: string;
  } | null>(null);
  const repsResultsCurrent =
    repsSubmittedQuery !== null &&
    repsSubmittedQuery.firstName === repsFirstName.trim() &&
    repsSubmittedQuery.firstSurname === repsFirstSurname.trim() &&
    repsSubmittedQuery.secondSurname === repsSecondSurname.trim();
  // What evidence about this practitioner points at (practice-profiles ticket
  // 09). `null` is the ordinary state — nothing found, the lookup failed, or it
  // has not landed yet — and all three mean the same thing to the screen:
  // nothing is checked, so the slide leaves once the lookup is over
  // (`practiceRecognisedNothing`). It is deliberately NOT in the flow store:
  // it is a derivation of the Specialty, re-asked when that changes, and
  // persisting it would make it survive a change of answer it no longer matches.
  const [practicePreSelection, setPracticePreSelection] =
    useState<PracticePreSelection | null>(null);
  // What the practice slide is DOING (practice-profiles ticket 09). The slide is
  // no longer one screen: the agent needs about ten seconds, and that wait is a
  // different screen rather than a flag on the picker.
  //
  //   inferring — the waiting screen, and no footer to act on
  //   picking   — the list, with whatever the agent recognised pre-checked and
  //               pinned to the top, for the practitioner to confirm; with
  //               nothing recognised, the slide leaves instead
  //
  // There is no third state where the answer is applied without being shown: a
  // recognition is a proposal, and the practitioner confirms it. What the agent
  // found rides in `practicePreSelection` rather than here — this holds only
  // which screen is up.
  //
  // Keyed by Specialty for the same reason the phase and the offer's deadline
  // are: an answer about one catalogue says nothing about the next.
  const [practiceInference, setPracticeInference] = useState<{
    specialty: string;
    status: "inferring" | "picking";
  } | null>(null);
  // The Specialty the in-flight lookup was asked about — never rendered, only
  // compared, so a ref rather than state (the same reason the RPPS provenance
  // below is one).
  const practiceLookupRef = useRef<string | null>(null);
  const practiceLookupGeneration = useRef(0);
  // Provenance of the currently-confirmable RPPS profile, read only when firing
  // the `hcp_registry_profile_confirmed` analytics event (never rendered) — hence refs.
  const registryMatchModeRef = useRef<RegistryLookupMode>("name");
  const registryFromMultipleRef = useRef(false);

  /**
   * Seed the address from navigation state, and let it WIN over the store.
   *
   * The surface that sent the user here (the `/welcome` sheet, `/signup`) has
   * just been told which address this signup is for, so it is the fresher
   * answer. An address in the store that disagrees belongs to an earlier run
   * abandoned in the same tab — a different signup — so the whole flow is reset
   * around the new one rather than half-carried.
   *
   * The guard used to be "only when the store is empty", which was harmless
   * while the address was asked for again on a slide. It is not any more: the
   * carousel now mails a code to the address it holds before showing anything,
   * so a stale one sends the code to a mailbox the user is not watching and the
   * six boxes wait for a code that will never come.
   *
   * Equal addresses keep the run — that is a reload after the code is
   * accepted, which must resume rather than restart.
   *
   * The desktop gate hands over without an address. When its proof is gone, a
   * reload has abandoned that handover, but `entrance: "desktop-gate"` stays in
   * the store and the sheet may then send the same address. Kept, that mark
   * turns `entersFromDesktopGate` on at a reload after the account is
   * created, and the code and intro slides drop out of the progress bar behind
   * the user. A handover still live at mount keeps its mark, for the same
   * reason in the other direction.
   *
   * A proof in memory for another address goes too. The name slide creates the
   * account from the proof's address, so a handover abandoned without Back
   * would create an account for an address the practitioner did not give here.
   */
  useEffectOnce(() => {
    if (arrivalEmail === null) return;
    if (otpCredential !== null && otpCredential.email !== arrivalEmail)
      setOtpCredential(null);
    if (flow.email !== "" && arrivalEmail !== flow.email) resetOnboardingFlow();
    updateOnboardingFlow(
      entersFromDesktopGate
        ? { email: arrivalEmail }
        : { email: arrivalEmail, entrance: "carousel" }
    );
  }, []);
  // --- Room invite state ---
  const [showOAuthModal, setShowOAuthModal] = useState(false);
  const startRoomOAuth = useStartRoomOAuth();
  const oauthRoomConfig = invitedRoom
    ? OauthRoomsConfig.find((r) => r.id === invitedRoom.id)
    : undefined;
  // INTERFACE locale: the invite flow runs before the reader is in the room, so the
  // interface language is the only signal available.
  const roomName =
    invitedRoom === null ? "" : getRoomName(invitedRoom, activeLang);
  const roomLogoUrl = invitedRoom !== null ? getRoomLogoUrl(invitedRoom) : null;

  // --- Dynamic step count ---
  // Carousel form steps stay in the mobile DOM. The registry slide contributes
  // a progress segment only for HCPs in a supported country. The count reflows
  // when the country and HCP answer are known.
  const visitsRegistry =
    slideAfterCountry({
      country,
      isHealthcareProfessional,
      npiPrefillEnabled,
      bePrefillEnabled,
      nlPrefillEnabled,
      ptPrefillEnabled,
      dkPrefillEnabled,
      esPrefillEnabled,
    }) === REGISTRY_SLIDE;

  /**
   * Whether the code slide is part of THIS run of the flow.
   *
   * Deliberately independent of whether the code has already been entered: it
   * decides a segment of the progress bar, and a bar that loses a segment behind
   * the user is a bar that jumps. What being past the code changes is where Back
   * goes, not how many steps there are.
   */
  const visitsOtp = !isReturningUser && !entersFromDesktopGate;

  /**
   * Whether the intro slide is part of this run.
   *
   * The desktop gate replaces it: its card carries the product promise, and its
   * legal line carries the terms and the healthcare-profession certification
   * the intro's two CTAs used to collect. Everywhere else it always runs.
   */
  const visitsIntro = !entersFromDesktopGate;

  /**
   * The address this run is FOR.
   *
   * Navigation state when the surface that sent the user here named one, the
   * store otherwise. Read straight from the router rather than through the
   * store, because the store is reconciled one effect later: on the first
   * commit it still holds whatever an abandoned run left in this tab, and the
   * code request below fires on that same commit. Reading the store there mailed
   * the code to the previous address while the screen printed the new one.
   */
  const signupEmail = arrivalEmail !== null ? arrivalEmail : email;

  /**
   * Ask for the code the slide is about to wait for.
   *
   * The carousel OPENS on that slide in the arm, so there is no Continue for the
   * user to press: the request belongs to arriving. It waits for the address,
   * which arrives from navigation state one effect after mount — asking inside
   * `initialize()` read an empty string and sent nothing, leaving the six boxes
   * waiting on a code nobody had asked for.
   *
   * Ref-guarded, so a re-render never doubles it. A RELOAD asks again, and
   * `requestCode` resumes the code this device already had mailed while it is
   * still valid, so the one in the mailbox keeps working. Only an explicit
   * Resend mails another.
   */
  const otpRequestedRef = useRef(false);
  useEffect(() => {
    if (otpRequestedRef.current) return;
    // Not before the session is known. `initialize()` answers that one tick
    // after mount, and until it has, an arrival that is ALREADY signed in is
    // indistinguishable from a fresh signup: `visitsOtp` still reads true and
    // `accountCreated` still reads false. Mailing on that commit would send a
    // signup code to someone who has an account — and count them into
    // `sign_up_started` — on the strength of not having asked yet.
    if (isLoading) return;
    if (step !== OTP_SLIDE || !visitsOtp || accountCreated) return;
    if (signupEmail === "") return;
    otpRequestedRef.current = true;
    // The code slide IS the start of signup now, so `sign_up_started` is fired
    // here rather than on the first keystroke of the name slide two screens
    // later. Left there, the funnel's denominator would exclude everyone who
    // abandons on the code screen — which is the one drop-off this whole change
    // introduces, and the one the PRD's guardrail exists to watch.
    handleSignupStarted();
    const askedFor = signupEmail;
    void (async () => {
      const outcome = await otp.requestCode(askedFor);
      // A trusted device proved this mailbox already. The slide is crossed
      // without a mail, on the same path a verified code takes.
      if (outcome.outcome === "trusted") acceptOtpCredential(askedFor, outcome);
    })();
  }, [isLoading, step, visitsOtp, accountCreated, signupEmail, otp]);

  function resolveStepName(s: number): SignupStepName {
    const name = CAROUSEL_STEP_NAMES[s];
    if (name === undefined)
      throw new Error(`Invariant: invalid carousel step index ${s}`);
    return name;
  }

  function goToStep(next: number): void {
    setStep(next);
    // Each visit to the profession slide starts on the short list. A selected
    // profession from the longer list stays listed (`pickerProfessions`).
    if (next !== PROFESSION_SLIDE) setProfessionList("collapsed");
    if (
      next === PARTNER_OBJECTION_SLIDE &&
      flow.partnerObjectionStage === "none"
    )
      updateOnboardingFlow({ partnerObjectionStage: "shown" });
    gaEventSignupStepViewed({
      step: resolveStepName(next),
      // The slide index IS the canonical analytics index — `CAROUSEL_STEP_NAMES`
      // is the single step order, so a skipped slide never renumbers the rest.
      step_index: next,
      signup_flow: CAROUSEL_STEP_NAMES,
      is_invited_to_room: isInvitedToRoom,
    });
  }

  // API queries for professions and specialties
  const professionsQuery = useApiQuery("/webapp/public/professions", {
    durable: true,
  });
  const professions = useMemo(
    () =>
      professionsQuery.state === "success"
        ? professionsQuery.body.filter(
            (p) => p.onboardingVisibility !== "hidden"
          )
        : [],
    [professionsQuery]
  );

  const specialtiesQuery = useApiQuery("/webapp/public/tags/parents", {
    durable: true,
  });
  const specialties = useMemo(
    () =>
      specialtiesQuery.state === "success"
        ? specialtiesQuery.body.filter((s) => !HIDDEN_SPECIALTY_IDS.has(s.id))
        : [],
    [specialtiesQuery]
  );

  // Specialties filtered to the ones associated with the selected profession.
  // Falls back to the full list when no profession is selected yet, or when the
  // profession is unknown / has no associated specialties.
  const specialtiesForSelectedProfession = useMemo(() => {
    const profession = professions.find((p) => p.uid === selectedProfession);
    if (profession === undefined) return specialties;
    if (profession.medical_specialties.length === 0) return specialties;
    const allowedTagIds = new Set(profession.medical_specialties);
    return specialties.filter((s) => allowedTagIds.has(s.id));
  }, [professions, selectedProfession, specialties]);

  // The rows the specialty step shows: the string layer first, then what the
  // model recognized behind an abbreviation nobody wrote down. Held HERE rather
  // than in the step, because both carousels render that step inside a
  // conditional and a hook cannot live there.
  const specialtyRows = useSpecialtySearch({
    specialties: specialtiesForSelectedProfession,
    query: specialtySearch,
  });

  // A profession whose catalogue holds exactly ONE specialty leaves nothing to
  // choose: that specialty is auto-selected and its step is skipped. Driven by
  // the data (today it is the veterinary professions → Animal Health), never by
  // a hardcoded uid list that would drift from the backend. `undefined` while
  // the catalogue is still loading (empty list) or when there is a real choice —
  // so nothing is auto-selected/skipped before the real list is known.
  // A PURE derivation (no effect): it holds regardless of HOW the profession was
  // set (manual tap or a registry pre-fill that skips the profession step) and never
  // leaves a stale auto-selection behind when the profession later changes.
  const soleSpecialty =
    specialtiesQuery.state === "success" &&
    professionsQuery.state === "success" &&
    specialtiesForSelectedProfession.length === 1
      ? specialtiesForSelectedProfession[0]
      : undefined;
  // The single source consulted wherever the specialty is displayed/persisted.
  const effectiveSpecialty =
    soleSpecialty !== undefined
      ? soleSpecialty.uid
      : professionsQuery.state === "success" &&
          specialtiesQuery.state === "success" &&
          !specialtiesForSelectedProfession.some(
            (specialty) => specialty.uid === selectedSpecialty
          )
        ? ""
        : selectedSpecialty;
  // A specialty the registry record already named is not worth asking again — but
  // only while the profession the practitioner ends up declaring actually
  // offers it. Declare a profession the registry specialty does not belong to
  // and the question comes back, because the pre-filled answer is no longer one
  // the finalize PATCH could set.
  const registrySpecialtyAnswers =
    registrySpecialty.length > 0 &&
    specialtiesForSelectedProfession.some((s) => s.uid === registrySpecialty);
  const selectedProfessionAvailable =
    selectedProfession !== null &&
    professions.some((profession) => profession.uid === selectedProfession);
  const confirmedCompleteProfile =
    professionsQuery.state === "success" &&
    specialtiesQuery.state === "success" &&
    selectedProfessionAvailable &&
    selectedProfession === registryProfession &&
    registrySpecialtyAnswers &&
    effectiveSpecialty === registrySpecialty;
  const hasProfessionStep =
    !confirmedCompleteProfile || registryProfileMode === "editing";
  // Whether the specialty step is part of the flow at all. The mobile carousel
  // keeps its slide mounted for the fixed translateX math, so it also gates the
  // slide's CONTENT — otherwise the skipping jump (profession → finalize) would
  // scroll across the very screen being skipped.
  const hasSpecialtyStep =
    soleSpecialty === undefined &&
    (!registrySpecialtyAnswers || registryProfileMode === "editing");
  // How the declared Specialty is CALLED, for the practice screen's recap row.
  // Null while the reference data is still loading — the row then shows the axis
  // answer alone rather than a uid.
  const effectiveSpecialtyModel = specialties.find(
    (specialty) => specialty.uid === effectiveSpecialty
  );
  const effectiveSpecialtyLabel =
    effectiveSpecialtyModel === undefined
      ? null
      : getItemTranslation(effectiveSpecialtyModel);

  // --- Practice Profiles (practice-profiles ticket 08) ---
  // Which arm this signup belongs to: whether the Specialty declared on slide 6
  // has a catalogue. Asked here and not only on the rail: the question costs an
  // agent lookup per signup, so the arm decides whether it is asked AT ALL
  // rather than what is done with it afterwards.
  const signupArm = useSignupPracticeArm(effectiveSpecialty);
  const offersPractice = signupArm === "in";
  // Whether this signup gets the free-text way out of the practice question.
  const offersDeclaration = usePracticeDeclaration();

  // The catalogue of the Specialty just declared, from the PUBLIC endpoint, which
  // the settings editor reads too. Nothing user-specific is asked for here.
  //
  // Not `durable`: the answer depends on the Specialty selected a slide ago, and
  // a practitioner who steps back and changes it must be offered the new one.
  const practiceOfferQuery = useApiQuery(
    "/webapp/public/practice-profiles/offered",
    effectiveSpecialty.length === 0
      ? false
      : { query: { specialtyUid: effectiveSpecialty } }
  );

  // Everything this flow waits for before it knows whether a practice slide
  // exists. The arm reads this same offer (`useSignupPracticeArm`), so the two
  // settle together, under the ONE ceiling below: an endpoint that never
  // answers must not hold a signup on a waiting screen with no bound.
  //
  // Before a Specialty is declared this is false: the query is disabled, and a
  // disabled `useApiQuery` is `idle` rather than `pending`.
  const practiceWaiting =
    signupArm === "pending" || practiceOfferQuery.state === "pending";
  // `state: "error"` does NOT mean "never answered". `createQueryResult` checks
  // the error before the data (`apps/api/src/shared/query.ts`), so a refetch that
  // fails after a success reports an error while the cache still holds the body —
  // `refetchOnWindowFocus` produces exactly that for a practitioner who leaves
  // for their email code and comes back. Reading only `success` here is what
  // retired the slide under them: the offer went null, `hasPracticeStep` went
  // false, and the effect below sent them past the question with their answer
  // half-given. The cache is the last answer, whatever the last fetch did.
  const practiceOfferAnswered = getApiQueryData(
    "/webapp/public/practice-profiles/offered",
    { query: { specialtyUid: effectiveSpecialty } }
  );
  const practiceOffer =
    practiceOfferQuery.state === "success"
      ? practiceOfferQuery.body
      : practiceOfferQuery.state === "error"
        ? practiceOfferAnswered
        : null;

  // The request came back with no answer. Deliberately NOT folded into
  // `practiceWaiting`: a wait that is over is not a wait running out of time, and
  // reading the two as one is what let this outcome go uncounted — the deadline
  // was the only thing that emitted, and a failure clears the deadline.
  const practiceOfferFailed = practiceOfferQuery.state === "error";

  // The Specialty whose offer was given up on — the deadline having passed while
  // it was still in flight, or the request having come back with nothing. Held
  // as the Specialty rather than a boolean because it says two things at once,
  // and both are needed: that this Specialty's offer is no longer worth waiting
  // for, AND that a practitioner who steps back and picks a different one gets
  // their own full wait rather than inheriting an expired verdict.
  //
  // In the store, not React state: the verdict has to outlive a REMOUNT of this
  // hook, and one happens inside the run it measures — the Firebase auth-state
  // change at finalize. Held in state, it cleared there while the offer was
  // still in error with an empty cache, and the failure branch, which emits with
  // no clock, counted one abandonment twice.
  const practiceOfferAbandonedFor = flow.practiceOfferAbandonedFor;

  // Whether this Specialty's offer was started COLD — read from the store, where
  // the fast track writes it with the Specialty itself. Keyed by Specialty rather
  // than re-derived from `step`, which keeps moving: a ceiling read off the
  // current slide re-arms the timer on every navigation, and fresh seconds
  // granted on a later slide let a late answer insert the slide behind someone
  // already past it.
  //
  // Warm, the 3 s only decide whether a slide exists ahead, and the specialty
  // slide's dwell pays them. Cold, the practitioner is already on the slide, so
  // the only bound that matters is the wait they can see. Two ceilings and not
  // one raised constant: raising the warm one lets an 8 s answer insert a slide
  // behind someone already past it.
  const practiceOfferStartedCold =
    flow.practiceOfferStartedColdFor === effectiveSpecialty;
  useEffect(() => {
    if (effectiveSpecialty.length === 0) return;
    if (practiceOfferAbandonedFor === effectiveSpecialty) return;
    const abandonedAt = practiceOfferStartedCold ? "on_slide" : "before_slide";
    // A request that came back with nothing needs no clock: the offer is as
    // answered as it will ever get, and what the practitioner is offered — a
    // slide that never appears, or a visible wait that ends past the slide — is
    // exactly what the deadline hands them.
    //
    // EXCEPT when the cache already holds this Specialty's catalogue: an error
    // over an answer is a refetch that failed (`refetchOnWindowFocus` re-asks
    // when a practitioner comes back from their email code), and `state: "error"`
    // reports the two identically. Nobody was offered nothing here, and latching
    // the verdict would retire the slide under someone standing on it.
    if (
      practiceOfferFailed &&
      getApiQueryData("/webapp/public/practice-profiles/offered", {
        query: { specialtyUid: effectiveSpecialty },
      }) === null
    ) {
      updateOnboardingFlow({ practiceOfferAbandonedFor: effectiveSpecialty });
      gaEventPracticeOfferAbandoned({
        abandoned_at: abandonedAt,
        reason: "request_failed",
      });
      return;
    }
    if (!practiceWaiting) return;
    const timer = setTimeout(
      () => {
        updateOnboardingFlow({ practiceOfferAbandonedFor: effectiveSpecialty });
        gaEventPracticeOfferAbandoned({
          abandoned_at: abandonedAt,
          reason: "deadline",
        });
      },
      practiceOfferStartedCold
        ? PRACTICE_INFERENCE_DEADLINE_MS
        : PRACTICE_OFFER_DEADLINE_MS
    );
    return () => {
      clearTimeout(timer);
    };
  }, [
    effectiveSpecialty,
    practiceWaiting,
    practiceOfferFailed,
    practiceOfferAbandonedFor,
    practiceOfferStartedCold,
  ]);
  const practiceOfferAbandoned =
    practiceOfferAbandonedFor === effectiveSpecialty;
  // "Later" was pressed on this Specialty's question: finalize saves no answer.
  const practiceSkipped = flow.practiceSkippedFor === effectiveSpecialty;

  // Whether the slide is part of the flow at all. 93 of the ~95 Specialties have
  // no catalogue for the whole of this lot, and their practitioners must never
  // see an empty screen — nor have it eat a segment of the progress bar. A
  // still-loading offer is NOT a step: it normally settles long before the
  // specialty slide is submitted, and treating it as one would flash a segment
  // in and out.
  //
  // An ABANDONED offer is not a step either, even if it lands afterwards: past
  // the deadline the practitioner has already been let through on the promise
  // that there is no such slide, and honouring a late answer would insert one
  // behind them and re-number every segment after it.
  const hasPracticeStep =
    !practiceOfferAbandoned &&
    practiceOffer !== null &&
    practiceOffer.docs.length > 0;

  // Whether the agent is still reading. An inference belongs to the Specialty it
  // was asked about, and anything else — no lookup, a stale one, a Specialty
  // re-declared since — is over, which is the answer that needs no lookup to be
  // correct.
  //
  // NOT what the slide shows: that is `practiceStatus`, three screens down,
  // which needs the held answer to tell the recap from the list. This one is the
  // wait alone, and it is separate because the deadline below has to arm itself
  // before any of that is computed.
  const practiceAgentStatus: "inferring" | "picking" =
    practiceInference !== null &&
    practiceInference.specialty === effectiveSpecialty
      ? practiceInference.status
      : "picking";

  // The wait's ceiling. A response that never comes must not be able to hold a
  // signup: past the deadline the slide leaves as if nothing was recognised,
  // and the answer is no longer wanted.
  useEffect(() => {
    if (practiceAgentStatus !== "inferring") return;
    const askedFor = effectiveSpecialty;
    const timer = setTimeout(() => {
      // Ends the lookup's AUTHORITY, not only the wait. The Specialty check in
      // the lookup only drops answers about a DIFFERENT Specialty, so without
      // this a late answer about the right one still landed — and now that
      // evidence decides between two screens, landing meant swapping the
      // catalogue the practitioner was reading for the recap, under their eyes.
      practiceLookupRef.current = null;
      setPracticeInference({ specialty: askedFor, status: "picking" });
    }, PRACTICE_INFERENCE_DEADLINE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [practiceAgentStatus, effectiveSpecialty]);

  // The fast track's cost, paid here. A practitioner whose registry record maps
  // cleanly now goes through this slide, and at that instant the offer has only
  // just been re-keyed on the new Specialty — so `hasPracticeStep` is still
  // false, and whether there is anything to confirm is not yet known. The
  // waiting screen covers that gap; this is what ends it when the answer is "no
  // catalogue", rather than leaving someone on a screen for a question that will
  // never be asked.
  useEffect(() => {
    if (step !== PRACTICE_SLIDE) return;
    if (hasPracticeStep) return;
    // Bounded by `practiceOfferAbandoned`, which carries the cold ceiling here.
    // Tracks the ARM and the OFFER, never `practiceStatus`: "no catalogue" in
    // 300 ms must walk straight on rather than sit out the agent.
    if (practiceWaiting && !practiceOfferAbandoned) return;
    leavePractice();
    // `leavePractice` omitted: a fresh closure every render would re-run this
    // every render. It reads `isInvitedToRoom` (via `signup_step_viewed`), which
    // is written before the carousel and cleared only at finalize.
  }, [step, hasPracticeStep, practiceWaiting, practiceOfferAbandoned]);
  // The email code, registry, profession, specialty, and Practice Profiles
  // slides are conditional. Each drops a segment when it
  // is not visited, and every slide after it shifts down by one.
  const totalSteps =
    CAROUSEL_SLIDES -
    (visitsOtp ? 0 : 1) -
    (visitsIntro ? 0 : 1) -
    // Grouped onto another screen: still questions, no longer steps.
    (groupsQuestions ? 1 : 0) -
    (visitsRegistry ? 0 : 1) -
    (hasProfessionStep ? 0 : 1) -
    (hasSpecialtyStep && !groupsQuestions ? 0 : 1) -
    (hasPracticeStep ? 0 : 1);
  const progressStep =
    step -
    (visitsOtp || step <= OTP_SLIDE ? 0 : 1) -
    (visitsIntro || step <= INTRO_SLIDE ? 0 : 1) -
    (!groupsQuestions || step <= COUNTRY_SLIDE ? 0 : 1) -
    (visitsRegistry || step <= REGISTRY_SLIDE ? 0 : 1) -
    (hasProfessionStep || step <= PROFESSION_SLIDE ? 0 : 1) -
    ((hasSpecialtyStep && !groupsQuestions) || step <= SPECIALTY_SLIDE
      ? 0
      : 1) -
    (hasPracticeStep || step <= PRACTICE_SLIDE ? 0 : 1);

  // An answer belongs to the Specialty it was given about — a practitioner who
  // went back and re-declared has answered nothing here yet, and the new
  // Specialty's own evidence lookup is what the screen opens on.
  const {
    profileUids: practiceProfileUids,
    disambiguation: practiceDisambiguation,
    declaration: practiceDeclaration,
  } = practiceAnswerForSpecialty({
    held: practice,
    specialtyUid: effectiveSpecialty,
  });

  // The one binary question's answer: the practitioner's own, or the side their
  // official qualification named when it named exactly one. Derived once and
  // read by all three of the things that must agree about it — what is checked,
  // which option renders selected, and whether Continue is enabled.
  const practiceAnswer = effectivePracticeDisambiguation({
    answer: practiceDisambiguation,
    evidence: practicePreSelection,
  });

  // Memoised for its IDENTITY, not its cost: `canAdvance` now reads it, and a
  // fresh Set every render would recompute that memo every render.
  const practiceSelection = useMemo(
    () =>
      practiceOffer === null
        ? new Set<string>()
        : selectedProfileUids({
            explicit: practiceProfileUids,
            evidence: practicePreSelection,
          }),
    [practiceOffer, practiceProfileUids, practicePreSelection]
  );

  // What the agent named that this catalogue carries — the one definition, shared
  // with the settings editor so the two flows report the same measure.
  const practiceInferredCount = inferredPracticeCount({
    evidence: practicePreSelection,
    offer: practiceOffer,
  });

  // The search provenance of THIS Specialty's question. Another Specialty's picks
  // describe another catalogue, and reading them here would credit the field for
  // a practice ticked off a list this practitioner has left.
  const practiceSearchProvenance: {
    readonly viaSearch: ReadonlySet<string>;
    readonly viaAssist: ReadonlySet<string>;
  } =
    practiceSearchPicks === null ||
    practiceSearchPicks.specialty !== effectiveSpecialty
      ? { viaSearch: new Set(), viaAssist: new Set() }
      : practiceSearchPicks;

  // What the list is ORDERED by: the same computation with the practitioner's own
  // taps left out, which is the answer the screen arrived with. Deriving it rather
  // than snapshotting it into the store keeps the order from being a second piece
  // of truth about the selection — and it moves exactly when the baseline moves
  // (the evidence lookup landing), never when a row is toggled.
  const practiceCheckedOnOpen =
    practiceOffer === null
      ? new Set<string>()
      : selectedProfileUids({
          explicit: null,
          evidence: practicePreSelection,
        });

  // The screen opens on the axis question, EXCEPT when the answer is already
  // known — a registry qualification, or the side the recognised practices sit on
  // (`disambiguationValues`, derived server-side). Asking anyway would ask a
  // practitioner to retype their own qualification, or to answer a question the
  // very next screen answers on their behalf by showing an adult practice the
  // evidence recognised. An explicit move (answering, or tapping "Change") wins over this
  // default, which is what the stored value is for.
  const practiceScreenForSpecialty =
    practiceScreen !== null && practiceScreen.specialty === effectiveSpecialty
      ? practiceScreen
      : null;

  // Which of the slide's THREE screens is showing. One slide throughout: the
  // title band, the progress bar and the footer keep their place, and only the
  // panel under them changes (`PracticeAgentPanel`).
  //
  // The recap is the default arrival for anyone the agent recognised, and the
  // list is what "Edit" opens — the reverse of what shipped first, where the
  // agent's answer only ever arrived as ticks on nineteen rows and so reduced
  // nothing. `showsPracticeRecap` holds the conditions; here it only has to come
  // after the held answer, which is why this is not next to the wait above.
  //
  // The names the recap reads back: the agent's main practice first, since the
  // sentence names only the first few, then the catalogue's own order. Resolved
  // here rather than in the panel because the panel is presentational and holds
  // no catalogue — and both carousels (mobile and desktop) render it, so a
  // translation done in either would be the same translation done twice.
  const practiceRecapMainUid =
    practicePreSelection === null ? null : practicePreSelection.mainProfileUid;
  const practiceRecapNames =
    practiceOffer === null
      ? []
      : [
          ...practiceOffer.docs.filter(
            (doc) =>
              doc.uid === practiceRecapMainUid && practiceSelection.has(doc.uid)
          ),
          ...practiceOffer.docs.filter(
            (doc) =>
              doc.uid !== practiceRecapMainUid && practiceSelection.has(doc.uid)
          ),
        ].map((doc) => getItemTranslation(doc));

  // Whether a recap EXISTS for this practitioner — read twice, because Back
  // offers it again after the list.
  //
  // `practiceRecapNames` carries the other half of it: nothing to read back is
  // not a recap. The evidence can only name uids this Specialty's offer holds,
  // so an empty intersection is a response no route should produce, and it
  // leaves the slide as nothing recognised does.
  const practiceHasRecap =
    practiceRecapNames.length > 0 &&
    showsPracticeRecap({
      evidence: practicePreSelection,
      explicit: practiceProfileUids,
    });

  // This Specialty's lookup is over and checked nothing: no practice found, a
  // failure, or the deadline. The slide then leaves, and the question waits for
  // Explore. Keyed to a lookup actually asked for this Specialty, so neither the
  // specialty slide nor the late-arm lookup's first render can trigger it.
  const practiceRecognisedNothing =
    hasPracticeStep &&
    practiceInference !== null &&
    practiceInference.specialty === effectiveSpecialty &&
    practiceInference.status === "picking" &&
    practiceProfileUids === null &&
    practiceDisambiguation === null &&
    practiceDeclaration === null &&
    practiceInferredCount === 0;

  const practiceStatus: "inferring" | "recap" | "picking" =
    // The waiting screen stays up until the slide leaves: no frame of the list.
    practiceAgentStatus === "inferring" || practiceRecognisedNothing
      ? "inferring"
      : // A stored screen is the practitioner having gone to the list, so the
        // recap is behind them until Back clears it.
        hasPracticeStep &&
          practiceHasRecap &&
          practiceScreenForSpecialty === null
        ? "recap"
        : "picking";

  // Nothing recognised: the slide asks nothing at signup and leaves on its own.
  useEffect(() => {
    if (step !== PRACTICE_SLIDE) return;
    if (!practiceRecognisedNothing) return;
    deferPracticeQuestion("nothing_recognised");
    // `deferPracticeQuestion` omitted, as in the no-catalogue exit above.
  }, [step, practiceRecognisedNothing]);

  // The practice question reached this practitioner. The denominator of
  // everything the screen is judged on, and the only place the agent's yield is
  // reported — `practice_profiles_confirmed` fires at the finalize
  // and counts nobody who left in between.
  //
  // Keyed by Specialty, session-scoped: Back returns to the recap and the slide
  // re-renders on every tap, and a denominator counting either would produce
  // confirmation rates above 1. `inferring` is deliberately not a screen here —
  // the wait is not the question, and counting it would put everyone whose agent
  // timed out into the population that was asked.
  //
  // ⚠️ `step === PRACTICE_SLIDE` is the condition that makes the rest mean
  // anything, and it is NOT implied by `hasPracticeStep`. That one goes true the
  // moment the CATALOGUE lands, which its own comment says normally happens long
  // before the specialty slide is submitted — and the inference does not start
  // until that submit (`handleSpecialtySubmit` → `lookUpPractice`). Claimed on
  // the specialty slide, this event would spend its once-per-session claim on a
  // screen nobody was looking at, stamp `recognised_practice_count: 0` and
  // `pre_selection_source: "none"` for everyone, and then stay silent when the
  // recap it exists to count actually appears. The agent's yield would read as a
  // flat zero, which is the exact hole this event was written to close.
  //
  // Everything the payload needs, or null when no question is on screen. One
  // narrowed value rather than a guard here and a fallback inside the callback:
  // `hasPracticeStep` already proves the offer is non-null and non-empty, so a
  // `practiceOffer === null ? 0 : …` in the payload would be a fallback for safe
  // code (§1) on the field `recognised_practice_count` is read against — and a silent 0
  // there is unfalsifiable noise rather than a crash if the guard ever loosens.
  const practiceQuestionShown =
    step !== PRACTICE_SLIDE ||
    !hasPracticeStep ||
    practiceStatus === "inferring" ||
    practiceOffer === null
      ? null
      : { specialtyUid: effectiveSpecialty, offer: practiceOffer };

  useTrackOnMount(
    "partner-objection-shown",
    step === PARTNER_OBJECTION_SLIDE
      ? { status: "shown", key: "signup" }
      : { status: "hidden" },
    () => {
      gaEventPartnerObjectionShown({ surface: "signup" });
    }
  );

  useTrackOnMount(
    "practice-question-shown",
    practiceQuestionShown === null
      ? { status: "hidden" }
      : // The FLOW is part of the claim, not just the Specialty. The settings
        // editor emits this same event and the claim store is one per browser
        // session, so a bare Specialty key would let whichever screen came first
        // silence the other.
        {
          status: "shown",
          key: `signup:${practiceQuestionShown.specialtyUid}`,
        },
    () => {
      if (practiceQuestionShown === null)
        throw new Error("should not happen: impression fired with no question");
      gaEventPracticeQuestionShown({
        flow: "signup",
        screen: practiceStatus === "recap" ? "recap" : "picker",
        // From the closure, not the claim key, which now carries the flow too.
        specialty_uid: practiceQuestionShown.specialtyUid,
        // Through the shared definition, never `practiceCheckedOnOpen.size`: the
        // pre-selection endpoint answers with the agent's reading UNFILTERED, so
        // that size counts practices this catalogue does not carry — and the
        // campaign flow, which filters, would report a different number for the
        // same agent.
        recognised_practice_count: inferredPracticeCount({
          evidence: practicePreSelection,
          offer: practiceQuestionShown.offer,
        }),
        catalogue_size: practiceQuestionShown.offer.docs.length,
        pre_selection_source:
          practicePreSelection === null ? "none" : practicePreSelection.source,
      });
    }
  );

  // Read off the answer the screen ARRIVED with, never the live one. Since the
  // axis went multi-select, a tap answers without navigating — so a default
  // derived from `practiceAnswer` would flip to "profiles" the instant the first
  // species was checked, closing the question under the finger that was still
  // answering it and making a second species unreachable.
  const practiceArrivedAnswered =
    effectivePracticeDisambiguation({
      answer: null,
      evidence: practicePreSelection,
    }) !== null;
  const practicePhase = resolvePracticePhase({
    offer: practiceOffer,
    stored:
      practiceScreenForSpecialty === null
        ? null
        : practiceScreenForSpecialty.phase,
    arrivedAnswered: practiceArrivedAnswered,
  });
  const practiceSearch =
    practiceScreenForSpecialty === null
      ? ""
      : practiceScreenForSpecialty.search;

  // The practices a model recognizes behind what was typed, plus the corrected
  // catalogue-miss report. Held here rather than in the picker, which is
  // presentational and does not know which of its three hosts it is in.
  const practiceAssist = usePracticeSearchAssist({
    offer: practiceOffer,
    search: practiceSearch,
    // `effectiveSpecialty` is the empty string while nothing is chosen, which
    // this flow reads everywhere as "none". The hook is typed on `null` and the
    // route refuses the empty string, so the translation happens here rather
    // than as a second empty-string check one level down.
    specialtyUid: effectiveSpecialty.length === 0 ? null : effectiveSpecialty,
    flow: "signup",
  });

  function setPracticePhase(phase: PracticePickerPhase): void {
    setPracticeScreen({
      specialty: effectiveSpecialty,
      phase,
      // Leaving the list drops what was filtering it: coming back to a list
      // narrowed by a search typed before an answer changed reads as a catalogue
      // that lost most of itself.
      search: phase === "profiles" ? practiceSearch : "",
    });
  }

  // "Edit my practices" on the recap. Writing the screen is what moves the slide
  // off the recap (`showsPracticeRecap` reads it as "already been to the list"),
  // and the phase it writes is the one the slide would have opened on anyway —
  // so an agent that named practices without naming a side still asks its axis
  // question here, rather than handing over a list spanning every side at once.
  function handlePracticeEdit(): void {
    // Reported here and not from `confirmed_from`, which names the screen the
    // practitioner LEFT from and so counts only the ones who also finished
    // signup. This is the intent to correct the agent, whether or not it
    // survives the two slides that follow.
    gaEventPracticeRecapEditClicked({
      specialty_uid: effectiveSpecialty,
      // The same definition the impression and the confirmation use. Left on
      // `practiceCheckedOnOpen.size` this field counted the pre-selection
      // UNFILTERED, so a uid the catalogue dropped inflated it — and the recap
      // it is supposed to describe never named that practice.
      recognised_practice_count: practiceInferredCount,
      opens: practicePhase,
    });
    // Edit reaches the LIST only when nothing stands in front of it. With an
    // unanswered axis it opens that question instead, and the Continue under it
    // is what marks the catalogue (`handlePracticeSubmit`).
    if (practicePhase === "profiles")
      setPracticeCatalogueOpenedFor(effectiveSpecialty);
    setPracticePhase(practicePhase);
  }

  function setPracticeSearch(search: string): void {
    setPracticeScreen({
      specialty: effectiveSpecialty,
      phase: practicePhase,
      search,
    });
  }

  // Email validation — debounce the typed email, feed it to the check query,
  // derive the error message from the query result.
  const debouncedEmail = useDebouncedValue(email, 800);
  const emailToCheck = z.email().safeParse(debouncedEmail).success
    ? debouncedEmail
    : null;
  const { data: emailCheckResult, isLoading: isCheckingEmail } =
    useCheckEmailExistsQuery(emailToCheck);

  const emailError = useMemo((): string | null => {
    if (emailCheckResult === undefined || emailToCheck === null) return null;
    if (!emailCheckResult.isExisting) return null;
    // Don't show the error for the email we just created an account with.
    if (createdEmail !== null && emailToCheck === createdEmail) return null;
    return t(
      "An account associated with this email address already exists. Login with this email address or create a new account."
    );
  }, [emailCheckResult, emailToCheck, createdEmail, t]);

  // --- Current step button validity (drives the shared floating CTA) ---
  const isCurrentStepValid = useMemo(() => {
    switch (step) {
      case NAME_SLIDE: {
        const namesGiven =
          firstName.trim().length > 0 && lastName.trim().length > 0;
        // The country is on this screen too, so it gates this Continue.
        return groupsQuestions
          ? namesGiven && country in getCountriesList()
          : namesGiven;
      }
      case OTP_SLIDE: {
        return otpCode.length === OTP_CODE_LENGTH;
      }
      case COUNTRY_SLIDE: {
        return country in getCountriesList();
      }
      // The RPPS slide renders its own footer actions, never the shared
      // Continue, so its validity here is irrelevant. The intro slide has no
      // shared CTA either.
      case PROFESSION_SLIDE: {
        if (!selectedProfessionAvailable) return false;
        if (!groupsQuestions) return true;
        // The specialty is on this screen too, so it gates this Continue —
        // including the offer wait the specialty slide owns below, because
        // leaving early is what would insert the practice slide behind them.
        return (
          !hasSpecialtyStep ||
          (effectiveSpecialty.length > 0 &&
            (!practiceWaiting || practiceOfferAbandoned))
        );
      }
      case SPECIALTY_SLIDE: {
        // The offer is worth waiting for, but only for a moment: while it is in
        // flight `hasPracticeStep` is false, so leaving early would jump the
        // practitioner past a slide that then appears behind them, and the
        // progress bar would gain a segment mid-flow.
        //
        // Settling includes failing — a lookup that errors leaves the flow
        // exactly as it is today. And it includes giving up: past
        // PRACTICE_OFFER_DEADLINE_MS the wait ends whether or not the answer
        // ever comes, because an endpoint that hangs must not be able to trap a
        // signup on a screen. That was the rule this feature was given, and a
        // wait with no ceiling is the same trap one endpoint further in.
        return (
          effectiveSpecialty.length > 0 &&
          (!practiceWaiting || practiceOfferAbandoned)
        );
      }
      case PRACTICE_SLIDE: {
        // At least one practice, and the disjoint question answered if the
        // Specialty asks one. Both live in `canSubmitPracticeProfiles`, which
        // the settings editor reads too — this slide holds no rule of its own,
        // because two surfaces enforcing the same invariant separately is how
        // they come to disagree about it.
        //
        // The practice requirement is NEW and it is the reversal: an empty
        // selection used to be a real answer here. It stopped being one because
        // the catalogue is expert-reviewed, so "none describe me" reports a hole
        // in the catalogue and must not be recordable as a preference. What keeps
        // that from being a trap is the search rewrite of the same lot — the
        // practitioner can reach any practice in their Specialty, not just the
        // ones the axis answer left on screen.
        //
        // And nothing is answerable at all while the agent reads: the list is
        // not up yet, so Continue is present but disabled rather than absent —
        // the band's height is shared by every slide, and it must not move.
        if (practiceStatus === "inferring" || practiceOffer === null)
          return false;
        // The recap asks for nothing, so Continue is open on arrival. It saves
        // what the agent found through the SAME path the list uses — untouched,
        // `selectedProfileUids` falls back to the evidence and
        // `handleFinalizeComplete` writes it — so accepting here is not a second
        // way to answer this screen.
        if (practiceStatus === "recap") return true;
        // On the axis question this Continue is the way OUT of it, so only the
        // axis half of the rule applies: the practices that satisfy the other
        // half are on the screen behind this button.
        if (practicePhase === "axis")
          return canLeavePracticeAxisQuestion({
            offer: practiceOffer,
            disambiguation: practiceAnswer,
          });
        return canSubmitPracticeProfiles({
          offer: practiceOffer,
          disambiguation: practiceAnswer,
          selected: practiceSelection,
          declaration: practiceDeclaration,
        });
      }
      case PARTNER_OBJECTION_SLIDE: {
        // "Continue" works with the box checked or not (REQ-02).
        return partnerObjectionAction.state.status === "idle" && !isFinalizing;
      }
      default: {
        return false;
      }
    }
  }, [
    step,
    groupsQuestions,
    hasSpecialtyStep,
    firstName,
    lastName,
    email,
    emailError,
    isCheckingEmail,
    country,
    otpCode,
    isReturningUser,
    selectedProfessionAvailable,
    effectiveSpecialty,
    practiceOffer,
    practiceWaiting,
    practiceOfferAbandoned,
    practiceStatus,
    practiceAnswer,
    practicePhase,
    practiceSelection,
    practiceDeclaration,
    partnerObjectionAction.state,
    isFinalizing,
  ]);

  useEffectOnce(() => {
    void initialize();
  }, []);

  // When a returning user (already authenticated on mount) is loaded, skip to first incomplete step.
  // isReturningUser is only set when accountCreated was initially false (see initialize()),
  // so it already distinguishes returning users from mid-signup remounts.
  const hasSkippedRef = useRef(false);
  useEffect(() => {
    if (!isReturningUser || currentUser === null || hasSkippedRef.current)
      return;
    hasSkippedRef.current = true;

    // Populate form fields from existing user data (SSO pre-fill)
    setCreatedEmail(currentUser.email);
    updateOnboardingFlow({
      firstName: currentUser.firstname === null ? "" : currentUser.firstname,
      lastName: currentUser.lastname === null ? "" : currentUser.lastname,
      email: currentUser.email,
    });

    // Start onboarding from the beginning so SSO users go through every step
    // with their fields pre-filled. Only skip if fully onboarded already.
    if (
      currentUser.professionModel !== null &&
      currentUser.mainSpecialtyModel !== null
    ) {
      updateOnboardingFlow({
        accountCreated: false,
        firstName: "",
        lastName: "",
      });
      advanceAfterCarousel();
      return;
    }

    // SSO user landing on onboarding without a completed profile — this is a
    // genuine signup, fire the event now that we know the user is not fully
    // onboarded (avoids firing on OAuth sign-in for existing users).
    const firebaseUser = auth.currentUser;
    const firstProvider =
      firebaseUser === null ? undefined : firebaseUser.providerData[0];
    const providerId =
      firstProvider === undefined ? null : firstProvider.providerId;
    const accountType =
      providerId === "google.com"
        ? "google"
        : providerId === "apple.com"
          ? "apple"
          : "email";
    gaEventStartSignUp({
      account_type: accountType,
      origin: isInvitedToRoom ? "shared link" : "direct download",
    });
  }, [currentUser, isReturningUser, isInvitedToRoom]);

  async function initialize() {
    try {
      const isAuthentified = await isUserAuthentified();
      if (isAuthentified) {
        /**
         * Nobody who is already signed in has an address left to prove, so the
         * code slide is not part of their run — `visitsOtp` says so, and the
         * request effect refuses to mail them a code.
         *
         * The step machine has to agree, and it cannot decide this at mount:
         * the flow store is `sessionStorage` and the session is Firebase's, so
         * an arrival that has one and not the other reads `accountCreated:
         * false` and opens on the code slide. That arrival is not exotic — it
         * is every cold start of the native app after a signup was killed
         * halfway, which the onboarding guard sends straight back here. It left
         * the practitioner on six empty boxes waiting for a code nobody had
         * sent, one slide after they had entered the last one.
         *
         * The intro is where a run without the code slide begins, and it is
         * what the returning-user path below pre-fills its way through.
         */
        const resumeStep = step === OTP_SLIDE ? INTRO_SLIDE : step;
        setStep(resumeStep);
        if (!accountCreated) {
          // Returning user — skip completed steps when currentUser loads
          setIsReturningUser(true);
        }
        updateOnboardingFlow({ accountCreated: true });
        setLoading(false);
        gaEventSignupStepViewed({
          step: resolveStepName(resumeStep),
          step_index: resumeStep,
          signup_flow: CAROUSEL_STEP_NAMES,
          is_invited_to_room: isInvitedToRoom,
        });
        return;
      }
      setLoading(false);
      gaEventViewSignUp();
      gaEventSignupStepViewed({
        step: resolveStepName(step),
        step_index: step,
        signup_flow: CAROUSEL_STEP_NAMES,
        is_invited_to_room: isInvitedToRoom,
      });
    } catch (error) {
      console.error("Couldn't mount signup page.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
      history.replace("/login");
    }
  }

  /**
   * Leave the funnel from a slide with nothing behind it inside the carousel.
   *
   * `history.goBack()` on its own is a silent no-op whenever onboarding IS the
   * first entry of the session (`history.length === 1`, the same read the
   * Explore gate's cross makes), and that is the common case rather than the
   * exotic one: the boot guards `replace` here for any account whose profile is
   * incomplete, so every cold start of the native app after a signup was killed
   * halfway lands with an empty back stack and a back arrow that does nothing.
   *
   * An authenticated practitioner cannot simply be pushed at the front door
   * either — `/welcome` bounces a session with an unfinished profile straight
   * back into onboarding (`AppRoutes`), which is the same dead arrow with more
   * steps. Backing out of a signup they have not finished IS signing out: the
   * account survives, and a fresh code opens it again. `useLogOutUser` owns
   * that door, including which one each layout gets.
   */
  function leaveOnboarding(): void {
    // The Firebase session, not `currentUser`: that one is also null while
    // `/users/me` is in flight, and `/welcome` bounces a live session back here.
    if (isAuthenticated) {
      void logOutUser();
      return;
    }
    // No session: the run is abandoned, so its answers and the proved code go
    // with it. Left behind, the desktop handover would let the next signup in
    // this tab skip its code and create the account for this address. The
    // surface that sent them here is still behind them, with the address they
    // typed on it.
    resetOnboardingFlow();
    setOtpCredential(null);
    otp.reset();
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.replace("/welcome");
  }

  function handleBack(): void {
    // Both entry slides leave onboarding rather than walk backwards inside it:
    // the intro is the first slide out of the arm, and in it the intro sits
    // behind a code that is already spent and an account that already exists.
    if (
      step === INTRO_SLIDE ||
      step === OTP_SLIDE ||
      // The first slide of a run handed over by the desktop gate. The slides
      // before it happened in a modal on another route, so there is nothing to
      // step back onto — going to `step - 1` would land on the intro, a screen
      // this practitioner never saw, and its CTAs would answer the HCP gate a
      // second time.
      (entersFromDesktopGate && step === NAME_SLIDE) ||
      // The name slide of a run that opened on an existing account without a
      // code: every cold start of the native app after a signup was killed
      // halfway, and every SSO or returning arrival. The run began on the
      // intro, which has no Back, so this is the first slide that can offer a
      // way out. A run that came through the code slide still steps back onto
      // its intro.
      (accountCreated && !flow.otpVerified && step === NAME_SLIDE)
    ) {
      leaveOnboarding();
      return;
    }
    gaEventSignupStepBack({
      step: resolveStepName(step),
      signup_flow: CAROUSEL_STEP_NAMES,
      is_invited_to_room: isInvitedToRoom,
    });
    if (step === REGISTRY_SLIDE) {
      discardRegistrySelection(null);
      registryAutoLookupRef.current = false;
      setRegistryState(null);
      setRegistryId("");
      setRegistryInitials("");
    }
    if (
      step === PRACTICE_SLIDE &&
      // Only for someone who actually walked the axis question on this slide.
      // A practitioner whose registry record settled it never saw that screen, so
      // sending them "back" to it would be sending them somewhere new — their
      // back button belongs to the specialty slide, and the recap row's "Change"
      // is how they reach the question if they want it.
      //
      // A stored screen used to be proof of that walk, because answering the
      // axis was the only thing that wrote one. "Edit my practices" is the
      // second writer: it stores the list over an axis the evidence had already
      // settled, which is the usual path on the two catalogued Specialties — so
      // the phase alone reads a recap reader as someone returning from a
      // question they never saw. `practiceArrivedAnswered` is exactly "the slide
      // opened on the axis" (`resolvePracticePhase`), so its negation is the
      // walk.
      practiceArrivedAnswered === false &&
      practiceScreenForSpecialty !== null &&
      practiceScreenForSpecialty.phase === "profiles" &&
      practiceOffer !== null &&
      practiceOffer.disambiguation !== null
    ) {
      // The practice slide holds two questions, so its back button walks the
      // second one back to the first before it leaves the slide. Without this,
      // the only way back to an axis answered by mistake would be the recap
      // row's "Change" — discoverable, but not where a user's thumb goes.
      setPracticePhase("axis");
      return;
    }
    if (
      step === PRACTICE_SLIDE &&
      // Only for someone who LEFT a recap. Clearing the stored screen is what
      // brings it back (`practiceStatus`), and `practiceHasRecap` goes false the
      // moment a row is tapped — so Back stops offering it as soon as the
      // practitioner has an answer of their own, and walks off the slide instead.
      practiceScreenForSpecialty !== null &&
      practiceHasRecap
    ) {
      setPracticeScreen(null);
      return;
    }
    if (
      (step === PRACTICE_SLIDE ||
        (step === PARTNER_OBJECTION_SLIDE &&
          (!hasPracticeStep || practiceRecognisedNothing))) &&
      confirmedCompleteProfile &&
      registryProfileMode === "confirmed"
    ) {
      updateOnboardingFlow({ registryProfileMode: "editing" });
      goToStep(PROFESSION_SLIDE);
      return;
    }
    // The registry slide is only a place to go back TO when it has a result to
    // show. On the grouped form a lookup that found nothing never rendered a
    // screen at all — it toasted and moved on — so stepping back onto it would
    // be stepping onto a screen the practitioner has never seen, and onto the
    // manual registry-id field that form deliberately does not offer.
    const registryHasAScreen =
      registryState !== null &&
      (registryState.status === "found" ||
        registryState.status === "multiple" ||
        registryState.status === "page" ||
        ((registry === "reps" || registry === "big") &&
          registryState.status === "editing") ||
        registryState.status === "too_many_results" ||
        (registry === "big" && registryState.status === "not_found"));
    if (
      groupsQuestions &&
      (step === REGISTRY_SLIDE ||
        (step === PROFESSION_SLIDE && (!visitsRegistry || !registryHasAScreen)))
    ) {
      // The country was answered on the name screen, so the next screen goes
      // back there. The country slide does not exist in this layout.
      goToStep(NAME_SLIDE);
    } else if (step === COUNTRY_SLIDE) {
      // The country rides on the name screen when the questions are grouped,
      // and the name slide is the one before it either way.
      goToStep(NAME_SLIDE);
    } else if (step === PROFESSION_SLIDE && !visitsRegistry) {
      // The registry slide was skipped on the way in; skip it on Back too.
      goToStep(COUNTRY_SLIDE);
    } else if (
      step === PARTNER_OBJECTION_SLIDE &&
      (!hasPracticeStep || practiceRecognisedNothing)
    ) {
      // The practice slide was jumped over (no catalogue for this Specialty, or
      // it left on its own because nothing was recognised: landing on it would
      // send the practitioner straight forward again) — and possibly the specialty slide too, for a single-specialty
      // profession, or because the grouped form asked it on the profession
      // screen.
      goToStep(
        hasSpecialtyStep && !groupsQuestions
          ? SPECIALTY_SLIDE
          : PROFESSION_SLIDE
      );
    } else if (
      step === PRACTICE_SLIDE &&
      (!hasSpecialtyStep || groupsQuestions)
    ) {
      goToStep(PROFESSION_SLIDE);
    } else {
      goToStep(step - 1);
    }
  }

  function handleSignupStarted() {
    if (isReturningUser) return;
    setSignupStarted(true);
    gaEventStartSignUp({
      account_type: "email",
      origin: isInvitedToRoom ? "shared link" : "direct download",
    });
  }

  // Search the selected country’s directory by name. A failure permits manual entry.
  /**
   * Enter the lookup for an arrival that finds none in flight.
   *
   * The registry slide renders nothing without an `registryState`, so any way of
   * REACHING it that is not `leaveCountry()` used to be a dead screen: a reload
   * that resumes on it, and stepping back onto it after a reload. The
   * transition is not the only way in, so it cannot be the only place the
   * request is made.
   *
   * Ref-guarded so a re-render never doubles the request; cleared whenever the
   * state goes back to null, which is what a fresh country submit does.
   */
  const registryLookupGeneration = useRef(0);
  const registryPageLoadingRef = useRef(false);
  const registryAutoLookupRef = useRef(false);
  const registryLookupContext = useRef({ country, eligible: visitsRegistry });

  function discardRegistrySelection(identity: typeof registryIdentity): void {
    const previous = onboardingFlowStore.get();
    const clearProfession =
      previous.registryProfession.length > 0 &&
      previous.profession === previous.registryProfession;
    const clearSpecialty =
      previous.registrySpecialty.length > 0 &&
      previous.specialty === previous.registrySpecialty;
    const clearPractice =
      clearSpecialty &&
      previous.practice !== null &&
      previous.practice.specialtyUid === previous.registrySpecialty;
    registryLookupGeneration.current += 1;
    registryPageLoadingRef.current = false;
    if (clearSpecialty) practiceLookupGeneration.current += 1;
    practiceLookupRef.current = null;
    setPendingRegistryConfirmation(null);
    setPracticePreSelection(null);
    setPracticeInference(null);
    updateOnboardingFlow({
      registryIdentity: identity,
      hcpDirectorySelection: null,
      registryVerified: false,
      registryProfession: "",
      registrySpecialty: "",
      registryProfileMode: "confirmed",
      employer: "",
      profession: clearProfession ? null : previous.profession,
      specialty: clearSpecialty ? "" : previous.specialty,
      practice: clearPractice ? null : previous.practice,
      practiceOfferStartedColdFor: clearSpecialty
        ? ""
        : previous.practiceOfferStartedColdFor,
      practiceOfferAbandonedFor: clearSpecialty
        ? ""
        : previous.practiceOfferAbandonedFor,
    });
  }

  useEffect(() => {
    const previous = registryLookupContext.current;
    if (previous.country === country && previous.eligible === visitsRegistry)
      return;
    registryLookupContext.current = { country, eligible: visitsRegistry };
    registryLookupGeneration.current += 1;
    registryPageLoadingRef.current = false;
    registryAutoLookupRef.current = false;
    setRegistryState(null);
    setRegistryId("");
    setRegistryInitials("");
    setStpsSearchMode("name");
    if (!visitsRegistry) discardRegistrySelection(null);
  }, [country, visitsRegistry]);
  useEffect(() => {
    if (step === REGISTRY_SLIDE && !visitsRegistry) {
      registryAutoLookupRef.current = false;
      setRegistryState(null);
      setRegistryId("");
      setRegistryInitials("");
      goToStep(PROFESSION_SLIDE);
      return;
    }
    if (step !== REGISTRY_SLIDE || !visitsRegistry) {
      registryAutoLookupRef.current = false;
      return;
    }
    if (registryState !== null || registryAutoLookupRef.current) return;
    registryAutoLookupRef.current = true;
    enterRegistryLookup({ mode: "automatic" });
  }, [step, visitsRegistry, registryState]);

  /**
   * The registry had nothing for this name.
   *
   * Keep the fallback on the registry step in both layouts so the practitioner
   * can see the manual profile action or enter an available registry number.
   * Settle the state instead of leaving a spinner that cannot resolve.
   */
  function settleRegistryNotFound(outcome: "not_found" | "unavailable"): void {
    setRegistryState({ status: outcome });
  }

  function enterRegistryLookup(
    lookup: { mode: "automatic" } | { mode: "manual"; initials: string }
  ): void {
    if (registry === null)
      throw new Error("Registry lookup requires a supported country");
    if (registry === "stps" && lookup.mode === "automatic") {
      setStpsName(`${firstName.trim()} ${lastName.trim()}`.trim());
      setStpsProfessionCode("");
      setStpsSearchMode("name");
    }
    const confirmRepsSurnames =
      registry === "reps" &&
      lookup.mode === "automatic" &&
      (/\s/u.test(lastName.trim()) ||
        lastName.trim().length < 2 ||
        lastName.trim().length > 150 ||
        firstName.trim().length > 150);
    const initials =
      registry !== "big"
        ? null
        : lookup.mode === "automatic"
          ? bigSearchInitialFromFirstName(firstName)
          : lookup.initials;
    if (registry === "big" && lookup.mode === "automatic")
      setRegistryInitials(initials === null ? "" : initials);
    if (registry === "reps" && lookup.mode === "automatic") {
      setRepsFirstName(firstName.trim());
      setRepsFirstSurname(lastName.trim());
      setRepsSecondSurname("");
    }
    if (registry === "reps")
      setRepsSubmittedQuery(
        lookup.mode === "automatic"
          ? {
              firstName: firstName.trim(),
              firstSurname: lastName.trim(),
              secondSurname: "",
            }
          : {
              firstName: repsFirstName.trim(),
              firstSurname: repsFirstSurname.trim(),
              secondSurname: repsSecondSurname.trim(),
            }
      );
    discardRegistrySelection(null);
    goToStep(REGISTRY_SLIDE);
    if (confirmRepsSurnames) {
      setRegistryState({ status: "editing" });
      return;
    }
    setRegistryState({ status: "loading" });
    const lookupGeneration = ++registryLookupGeneration.current;
    registryMatchModeRef.current = "name";
    registryFromMultipleRef.current = false;
    void (async () => {
      try {
        let state: RegistryLookupResult =
          registry === "rpps"
            ? registryStateFromRppsLookup(
                (
                  await apiClient.post(
                    "/webapp/public/onboarding/rpps-lookup",
                    {
                      json: {
                        mode: "name",
                        firstName: firstName.trim(),
                        lastName: lastName.trim(),
                        page: 1,
                      },
                    }
                  )
                ).body
              )
            : registry === "inami" ||
                registry === "big" ||
                registry === "om" ||
                registry === "stps" ||
                registry === "reps"
              ? registryStateFromDirectoryLookup(
                  (
                    await apiClient.post(
                      "/webapp/public/hcp-directory/search",
                      {
                        json:
                          registry === "stps"
                            ? {
                                registry: "stps",
                                mode: "name",
                                name:
                                  lookup.mode === "automatic"
                                    ? `${firstName.trim()} ${lastName.trim()}`.trim()
                                    : stpsName.trim(),
                                professionCode:
                                  lookup.mode === "automatic" ||
                                  stpsProfessionCode === ""
                                    ? null
                                    : stpsProfessionCode,
                              }
                            : registry === "reps"
                              ? {
                                  registry: "reps",
                                  mode: "name",
                                  firstName:
                                    lookup.mode === "automatic"
                                      ? firstName.trim()
                                      : repsFirstName.trim(),
                                  firstSurname:
                                    lookup.mode === "automatic"
                                      ? lastName.trim()
                                      : repsFirstSurname.trim(),
                                  secondSurname:
                                    lookup.mode === "automatic" ||
                                    repsSecondSurname.trim() === ""
                                      ? null
                                      : repsSecondSurname.trim(),
                                  page: 1,
                                }
                              : registry === "big"
                                ? {
                                    registry: "big",
                                    mode: "name",
                                    name: lastName.trim(),
                                    initials,
                                  }
                                : {
                                    registry:
                                      registry === "inami"
                                        ? "health_pro"
                                        : "om",
                                    mode: "name",
                                    name: `${firstName.trim()} ${lastName.trim()}`.trim(),
                                    page: 1,
                                  },
                      }
                    )
                  ).body
                )
              : registryStateFromRegistryLookup(
                  (
                    await apiClient.post(
                      "/webapp/public/onboarding/registry-lookup",
                      {
                        json: {
                          registry,
                          mode: "name",
                          firstName: firstName.trim(),
                          lastName: lastName.trim(),
                          page: 1,
                        },
                      }
                    )
                  ).body
                );
        if (lookupGeneration !== registryLookupGeneration.current) return;
        if (
          registry === "big" &&
          lookup.mode === "automatic" &&
          initials !== null &&
          state.status === "not_found"
        ) {
          state = registryStateFromDirectoryLookup(
            (
              await apiClient.post("/webapp/public/hcp-directory/search", {
                json: {
                  registry: "big",
                  mode: "name",
                  name: lastName.trim(),
                  initials: null,
                },
              })
            ).body
          );
          if (lookupGeneration !== registryLookupGeneration.current) return;
        }
        if (
          state.status === "not_found" ||
          state.status === "unavailable" ||
          (state.status === "page" &&
            state.candidates.length === 0 &&
            state.nextPage === null)
        ) {
          settleRegistryNotFound(
            state.status === "page" ? "not_found" : state.status
          );
        } else {
          setRegistryState(state);
        }
        gaEventRegistryLookupResult({
          registry,
          mode: "name",
          outcome:
            state.status === "page"
              ? state.candidates.length === 0 && state.nextPage === null
                ? "not_found"
                : "multiple"
              : state.status,
          candidate_count:
            state.status === "multiple" || state.status === "page"
              ? state.candidates.length
              : state.status === "found"
                ? 1
                : 0,
        });
      } catch {
        if (lookupGeneration !== registryLookupGeneration.current) return;
        gaEventRegistryLookupResult({
          registry,
          mode: "name",
          outcome: "unavailable",
          candidate_count: 0,
        });
        settleRegistryNotFound(
          registry === "rpps" ? "not_found" : "unavailable"
        );
      }
    })();
  }

  function loadMoreRegistryCandidates(): void {
    if (registry === "reps" && !repsResultsCurrent) return;
    if (
      registryState === null ||
      registryState.status !== "page" ||
      registryState.nextPage === null ||
      registryPageLoadingRef.current
    )
      return;
    if (registry === null || registry === "big" || registry === "stps") return;
    const page = registryState.nextPage;
    const lookupGeneration = registryLookupGeneration.current;
    registryPageLoadingRef.current = true;
    setRegistryState({ ...registryState, loadState: "loading" });
    void (async () => {
      try {
        const next =
          registry === "rpps"
            ? registryStateFromRppsLookup(
                (
                  await apiClient.post(
                    "/webapp/public/onboarding/rpps-lookup",
                    {
                      json: {
                        mode: "name",
                        firstName: firstName.trim(),
                        lastName: lastName.trim(),
                        page,
                      },
                    }
                  )
                ).body
              )
            : registry === "inami" || registry === "om" || registry === "reps"
              ? registryStateFromDirectoryLookup(
                  (
                    await apiClient.post(
                      "/webapp/public/hcp-directory/search",
                      {
                        json:
                          registry === "reps"
                            ? {
                                registry: "reps",
                                mode: "name",
                                firstName: repsFirstName.trim(),
                                firstSurname: repsFirstSurname.trim(),
                                secondSurname:
                                  repsSecondSurname.trim() === ""
                                    ? null
                                    : repsSecondSurname.trim(),
                                page,
                              }
                            : {
                                registry:
                                  registry === "inami" ? "health_pro" : "om",
                                mode: "name",
                                name: `${firstName.trim()} ${lastName.trim()}`.trim(),
                                page,
                              },
                      }
                    )
                  ).body
                )
              : registryStateFromRegistryLookup(
                  (
                    await apiClient.post(
                      "/webapp/public/onboarding/registry-lookup",
                      {
                        json: {
                          registry: "npi",
                          mode: "name",
                          firstName: firstName.trim(),
                          lastName: lastName.trim(),
                          page,
                        },
                      }
                    )
                  ).body
                );
        if (lookupGeneration !== registryLookupGeneration.current) return;
        if (next.status !== "page")
          throw new Error("Registry page search returned no page");
        setRegistryState((current) => {
          if (
            current === null ||
            current.status !== "page" ||
            current.nextPage !== page
          )
            return current;
          const seen = new Set(
            current.candidates.map((candidate) =>
              candidate.registry === "inami" ||
              candidate.registry === "om" ||
              candidate.registry === "stps" ||
              candidate.registry === "big" ||
              candidate.registry === "reps"
                ? candidate.sourceRecordId
                : candidate.identifier
            )
          );
          return {
            status: "page",
            candidates: [
              ...current.candidates,
              ...next.candidates.filter((candidate) => {
                const id =
                  candidate.registry === "inami" ||
                  candidate.registry === "om" ||
                  candidate.registry === "stps" ||
                  candidate.registry === "big" ||
                  candidate.registry === "reps"
                    ? candidate.sourceRecordId
                    : candidate.identifier;
                if (seen.has(id)) return false;
                seen.add(id);
                return true;
              }),
            ],
            nextPage: next.nextPage,
            loadState: "idle",
          };
        });
      } catch {
        if (lookupGeneration !== registryLookupGeneration.current) return;
        setRegistryState((current) =>
          current !== null &&
          current.status === "page" &&
          current.nextPage === page
            ? { ...current, loadState: "error" }
            : current
        );
      } finally {
        if (lookupGeneration === registryLookupGeneration.current)
          registryPageLoadingRef.current = false;
      }
    })();
  }

  function changeStpsName(value: string): void {
    if (value === stpsName) return;
    setStpsName(value);
    invalidateDirectorySearch();
  }
  function changeStpsProfessionCode(value: string): void {
    if (value === stpsProfessionCode) return;
    setStpsProfessionCode(value);
    invalidateDirectorySearch();
  }
  function changeStpsSearchMode(mode: "name" | "identifier"): void {
    if (mode === stpsSearchMode) return;
    setStpsSearchMode(mode);
    invalidateDirectorySearch();
  }
  function changeRegistryId(value: string): void {
    if (value === registryId) return;
    setRegistryId(value);
    if (registry === "stps") invalidateDirectorySearch();
  }
  function searchStpsAgain(): void {
    if (registry !== "stps") throw new Error("STPS search requires Denmark");
    if (stpsSearchMode !== "name")
      throw new Error("STPS name search requires name mode");
    if (stpsName.trim().length < 3) return;
    enterRegistryLookup({ mode: "manual", initials: "" });
  }
  function refineBigSearch(): void {
    if (registry !== "big")
      throw new Error("BIG initials refinement requires the BIG registry");
    const initials = registryInitials.trim();
    if (!BIG_INITIALS_PATTERN.test(initials)) return;
    enterRegistryLookup({ mode: "manual", initials });
  }

  function searchRepsAgain(): void {
    if (registry !== "reps") throw new Error("REPS search requires Spain");
    if (
      repsFirstName.trim().length > 150 ||
      repsFirstSurname.trim().length < 2 ||
      repsFirstSurname.trim().length > 150 ||
      repsSecondSurname.trim().length > 150
    )
      return;
    enterRegistryLookup({ mode: "manual", initials: "" });
  }

  function invalidateDirectorySearch(): void {
    discardRegistrySelection(null);
    setRegistryState({ status: "editing" });
  }

  function invalidateRepsSearch(): void {
    discardRegistrySelection(null);
    if (registryState !== null && registryState.status === "page") {
      if (registryState.loadState === "loading")
        setRegistryState({ ...registryState, loadState: "idle" });
      return;
    }
    if (registryState === null || registryState.status !== "multiple")
      setRegistryState({ status: "editing" });
  }

  function changeRepsFirstName(value: string): void {
    if (value === repsFirstName) return;
    setRepsFirstName(value);
    invalidateRepsSearch();
  }

  function changeRepsFirstSurname(value: string): void {
    if (value === repsFirstSurname) return;
    setRepsFirstSurname(value);
    invalidateRepsSearch();
  }

  function changeRepsSecondSurname(value: string): void {
    if (value === repsSecondSurname) return;
    setRepsSecondSurname(value);
    invalidateRepsSearch();
  }

  function changeRegistryInitials(value: string): void {
    if (registry !== "big")
      throw new Error("BIG initials correction requires the BIG registry");
    if (value === registryInitials) return;
    setRegistryInitials(value);
    invalidateDirectorySearch();
  }

  // The not-found fallback lets the user type their exact registry ID. An empty id
  // just skips the step; otherwise we resolve the record by its unique id — a
  // hit reveals the profile to confirm (and pre-fill from), a miss keeps the
  // typed id as the user's self-declared HCP identifier and moves on.
  function submitRegistryId(): void {
    if (registry === null)
      throw new Error("Identifier submission requires a registry");
    if (registry === "reps") {
      discardRegistrySelection(null);
      setRegistryState(null);
      gaEventRegistryStepResolvedManually({ registry, via: "skipped" });
      goToStep(PROFESSION_SLIDE);
      return;
    }
    const trimmed =
      registry === "stps" ? registryId.trim() : registryId.replace(/\s+/g, "");
    const normalizedIdentity = normalizeRegistryIdentifier({
      registry,
      identifier: trimmed,
    });
    if (
      trimmed.length > 0 &&
      (registry === "npi" ||
        registry === "inami" ||
        registry === "big" ||
        registry === "om" ||
        registry === "stps") &&
      !(registry === "inami" && /^[0-9]{8}$/.test(trimmed)) &&
      !isWellFormedRegistryIdentifier(normalizedIdentity)
    ) {
      displayToast(
        t("Enter a valid {{registry}} number.", {
          registry: HCP_REGISTRIES[registry].label,
        }),
        "warning"
      );
      return;
    }
    if (registry === "stps" && stpsSearchMode !== "identifier")
      throw new Error("STPS identifier search requires identifier mode");
    if (registry === "stps" && trimmed.length === 0) return;
    discardRegistrySelection(null);
    if (trimmed.length === 0) {
      setRegistryState(null);
      gaEventRegistryStepResolvedManually({ registry, via: "skipped" });
      goToStep(PROFESSION_SLIDE);
      return;
    }
    const lookupGeneration = ++registryLookupGeneration.current;
    setRegistryState({ status: "loading" });
    void (async () => {
      try {
        const state =
          registry === "rpps"
            ? registryStateFromRppsLookup(
                (
                  await apiClient.post(
                    "/webapp/public/onboarding/rpps-lookup",
                    { json: { mode: "rppsId", rppsId: trimmed } }
                  )
                ).body
              )
            : registry === "inami" ||
                registry === "big" ||
                registry === "stps" ||
                registry === "om"
              ? registryStateFromDirectoryLookup(
                  (
                    await apiClient.post(
                      "/webapp/public/hcp-directory/search",
                      {
                        json: {
                          registry:
                            registry === "inami" ? "health_pro" : registry,
                          mode: "identifier",
                          identifier:
                            registry === "stps"
                              ? normalizedIdentity.identifier
                              : trimmed,
                        },
                      }
                    )
                  ).body
                )
              : registryStateFromRegistryLookup(
                  (
                    await apiClient.post(
                      "/webapp/public/onboarding/registry-lookup",
                      {
                        json: {
                          registry,
                          mode: "identifier",
                          identifier: trimmed,
                        },
                      }
                    )
                  ).body
                );
        if (lookupGeneration !== registryLookupGeneration.current) return;
        if (state.status === "page")
          throw new Error("Identifier search returned a page");
        if (state.status === "inactive") {
          setRegistryState(state);
          gaEventRegistryLookupResult({
            registry,
            mode: "identifier",
            outcome: "inactive",
            candidate_count: 0,
          });
          return;
        }
        if (state.status === "found") {
          registryMatchModeRef.current = "identifier";
          registryFromMultipleRef.current = false;
          setRegistryState(state);
          gaEventRegistryLookupResult({
            registry,
            mode: "identifier",
            outcome: "found",
            candidate_count: 1,
          });
        } else if (state.status === "multiple") {
          setRegistryState(state);
          gaEventRegistryLookupResult({
            registry,
            mode: "identifier",
            outcome: "multiple",
            candidate_count: state.candidates.length,
          });
        } else {
          // Not in the directory — keep what they typed and continue. Settle
          // the state back to not_found (never leave it "loading", or stepping
          // back onto this slide would show an unresolvable spinner).
          setRegistryState(state);
          if (state.status === "unavailable")
            displayToast(
              t("The registry is unavailable. You can continue manually."),
              "warning"
            );
          gaEventRegistryLookupResult({
            registry,
            mode: "identifier",
            outcome: state.status,
            candidate_count: 0,
          });
          if (registry === "stps") return;
          gaEventRegistryStepResolvedManually({
            registry,
            via:
              registry === "inami" && trimmed.length !== 11
                ? "skipped"
                : "id_stored",
          });
          discardRegistrySelection(
            registry === "inami" && trimmed.length !== 11
              ? null
              : normalizedIdentity
          );
          goToStep(PROFESSION_SLIDE);
        }
      } catch {
        if (lookupGeneration !== registryLookupGeneration.current) return;
        gaEventRegistryLookupResult({
          registry,
          mode: "identifier",
          outcome: "unavailable",
          candidate_count: 0,
        });
        if (
          registry === "npi" ||
          registry === "inami" ||
          registry === "big" ||
          registry === "om" ||
          registry === "stps"
        )
          displayToast(
            t("The registry is unavailable. You can continue manually."),
            "warning"
          );
        // Lookup failed — don't block onboarding; keep the typed id and move on.
        setRegistryState({ status: "unavailable" });
        if (registry === "stps") return;
        gaEventRegistryStepResolvedManually({
          registry,
          via:
            registry === "inami" && trimmed.length !== 11
              ? "skipped"
              : "id_stored",
        });
        discardRegistrySelection(
          registry === "inami" && trimmed.length !== 11
            ? null
            : normalizedIdentity
        );
        goToStep(PROFESSION_SLIDE);
      }
    })();
  }

  // Confirm a registry profile. When both the matched profession AND specialty map
  // to selectable app entries, we have a complete profile straight from the
  // registry — pre-fill them and skip the profession + specialty steps entirely,
  // going right to the practice slide. Otherwise (partial / unmapped match)
  // we pre-fill what we can and fall through to the manual steps, so the user
  // never ends up finalizing with a profession/specialty the PATCH can't set.
  function confirmRegistryProfile(candidate: RegistryCandidateView): void {
    if (candidate.registry === "reps" && !repsResultsCurrent) return;
    if (
      professionsQuery.state === "error" ||
      specialtiesQuery.state === "error"
    ) {
      displayToast(t("Could not load profile choices. Try again."), "warning");
      void professionsQuery.refetch();
      void specialtiesQuery.refetch();
      return;
    }
    if (
      professionsQuery.state !== "success" ||
      specialtiesQuery.state !== "success"
    ) {
      setPendingRegistryConfirmation({
        candidate,
        lookupGeneration: registryLookupGeneration.current,
      });
      return;
    }
    commitRegistryProfile(candidate);
  }

  useEffect(() => {
    if (pendingRegistryConfirmation === null) return;
    if (
      step !== REGISTRY_SLIDE ||
      registry !== pendingRegistryConfirmation.candidate.registry ||
      registryLookupGeneration.current !==
        pendingRegistryConfirmation.lookupGeneration
    ) {
      setPendingRegistryConfirmation(null);
      return;
    }
    const cataloguesSettled =
      (professionsQuery.state === "success" &&
        specialtiesQuery.state === "success") ||
      professionsQuery.state === "error" ||
      specialtiesQuery.state === "error";
    if (!cataloguesSettled) return;
    setPendingRegistryConfirmation(null);
    confirmRegistryProfile(pendingRegistryConfirmation.candidate);
  }, [
    pendingRegistryConfirmation,
    step,
    registry,
    professionsQuery.state,
    specialtiesQuery.state,
  ]);

  function commitRegistryProfile(candidate: RegistryCandidateView): void {
    const registry = candidate.registry;
    const matchedProfession =
      candidate.matchedProfessionUid === null
        ? undefined
        : professions.find((p) => p.uid === candidate.matchedProfessionUid);
    const matchedSpecialty =
      candidate.matchedSpecialtyUid === null
        ? undefined
        : specialties.find((s) => s.uid === candidate.matchedSpecialtyUid);

    const specialtyCompatible =
      matchedProfession === undefined ||
      matchedSpecialty === undefined ||
      matchedProfession.medical_specialties.length === 0 ||
      matchedProfession.medical_specialties.includes(matchedSpecialty.id);
    const usableSpecialty = specialtyCompatible ? matchedSpecialty : undefined;
    const fastTracked =
      matchedProfession !== undefined && usableSpecialty !== undefined;
    gaEventRegistryProfileConfirmed({
      registry,
      match_mode: registryMatchModeRef.current,
      from_multiple: registryFromMultipleRef.current,
      fast_tracked: fastTracked,
    });

    // What the registry knows, on both branches: the id, where they work, and
    // the specialty it named (kept apart from `specialty`, which a manual tap
    // writes too — see the store).
    const registryFacts = {
      registryIdentity:
        candidate.identifier === null
          ? null
          : { registry: candidate.registry, identifier: candidate.identifier },
      hcpDirectorySelection:
        candidate.registry === "inami"
          ? {
              registry: "health_pro" as const,
              receipt: candidate.selectionReceipt,
            }
          : candidate.registry === "stps"
            ? { registry: "stps" as const, receipt: candidate.selectionReceipt }
            : candidate.registry === "big"
              ? {
                  registry: "big" as const,
                  receipt: candidate.selectionReceipt,
                }
              : candidate.registry === "om"
                ? {
                    registry: "om" as const,
                    receipt: candidate.selectionReceipt,
                  }
                : candidate.registry === "reps"
                  ? {
                      registry: "reps" as const,
                      receipt: candidate.selectionReceipt,
                    }
                  : null,
      registryVerified: candidate.registry !== "reps",
      registryProfession:
        matchedProfession === undefined ? "" : matchedProfession.uid,
      registrySpecialty:
        usableSpecialty === undefined ? "" : usableSpecialty.uid,
      registryProfileMode: "confirmed" as const,
      employer:
        candidate.organizationName === null ? "" : candidate.organizationName,
    };

    if (
      fastTracked &&
      matchedProfession !== undefined &&
      usableSpecialty !== undefined
    ) {
      updateOnboardingFlow({
        profession: matchedProfession.uid,
        specialty: usableSpecialty.uid,
        // In the SAME write as the Specialty, because this jump is what leaves
        // the offer no slide to fly across: the deadline cannot work that out on
        // its own later, and a verdict that emits without a clock cannot wait a
        // commit for it.
        practiceOfferStartedColdFor: usableSpecialty.uid,
        ...registryFacts,
      });
      // Complete profile straight from the registry — jump the profession and
      // specialty slides. NOT past the practice slide, though: these are the
      // practitioners whose practice is best known, and skipping them was
      // withholding the recognition from exactly the population it works on. The
      // waiting screen covers the moment when the offer for this Specialty is
      // still in flight, and a Specialty with no catalogue walks straight on
      // from there.
      if (signupArm === "in")
        lookUpPractice({
          specialtyUid: usableSpecialty.uid,
          registryIdentity: registryFacts.registryIdentity,
        });
      goToStep(PRACTICE_SLIDE);
      return;
    }

    updateOnboardingFlow({
      profession:
        matchedProfession !== undefined
          ? matchedProfession.uid
          : registryProfession.length > 0 &&
              selectedProfession === registryProfession
            ? null
            : selectedProfession,
      specialty:
        usableSpecialty !== undefined
          ? usableSpecialty.uid
          : registrySpecialty.length > 0 &&
              selectedSpecialty === registrySpecialty
            ? ""
            : selectedSpecialty,
      ...registryFacts,
    });
    goToStep(PROFESSION_SLIDE);
  }

  // The practice arm can settle after a complete registry confirmation. Start
  // its evidence lookup once, with the confirmed Specialty and identity.
  useEffect(() => {
    if (step !== PRACTICE_SLIDE || signupArm !== "in") return;
    if (!confirmedCompleteProfile || practiceInference !== null) return;
    lookUpPractice({
      specialtyUid: effectiveSpecialty,
      registryIdentity: availableRegistryIdentity,
    });
  }, [
    step,
    signupArm,
    confirmedCompleteProfile,
    practiceInference,
    effectiveSpecialty,
    availableRegistryIdentity,
  ]);

  function handleFinalSubmit(): void {
    if (!selectedProfessionAvailable) return;
    // The finalize now also runs in place on a question screen (the practice
    // list, the profession) whose rows stay live while it spins: an answer
    // given then must not start a second one.
    if (isFinalizing) return;

    // Persist selections so a reload resumes the finalize with them.
    updateOnboardingFlow({
      profession: selectedProfession,
      specialty: effectiveSpecialty.length > 0 ? effectiveSpecialty : "",
    });

    startFinalize();
  }

  // Enter the finalizing state (the last question keeps rendering, with its
  // CTA spinning): PATCH the name onto the account, then let the finalize effect
  // persist the profile. The profession/specialty are read fresh from the flow
  // store by that effect, so callers only need to have updated the store before
  // calling this.
  //
  // The account ALWAYS exists by the time this runs: an email signup created it
  // on the name slide with the uid the code minted, and an SSO or returning
  // arrival was signed in before the carousel opened. `handleNameNext` is what
  // holds that invariant — it never advances past the name without one.
  function startFinalize(): void {
    finalizeStartedRef.current = false;
    setFinalizing(true);
    updateOnboardingFlow({ finalizeStarted: true });
    const patchPromise = apiClient
      .patch("/webapp/private/users/me", {
        json: {
          firstname: firstName.trim(),
          lastname: lastName.trim(),
          country,
          ...(email.trim().length > 0 ? { email: email.trim() } : {}),
        },
      })
      .then(() => {
        updateOnboardingFlow({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
      })
      .catch((error: unknown) => {
        console.error("Failed to update name:", error);
      });
    accountReadyRef.current = patchPromise;
  }

  // Save profession + specialty + name/email once the account is ready, then
  // finish onboarding. Driven by the finalize effect below.
  async function handleFinalizeComplete(): Promise<void> {
    try {
      const professionObj = professions.find(
        (p) => p.uid === selectedProfession
      );
      const specialtyObj = specialties.find(
        (s) => s.uid === effectiveSpecialty
      );
      if (professionObj !== undefined && specialtyObj !== undefined) {
        await apiClient.patch("/webapp/private/users/me", {
          json: {
            firstname: firstName.trim(),
            lastname: lastName.trim(),
            country,
            ...(email.trim().length > 0 ? { email: email.trim() } : {}),
            ...(availableRegistryIdentity === null ||
            availableDirectorySelection !== null
              ? {}
              : {
                  hcpRegistryIds: [
                    ...(currentUser === null ? [] : currentUser.hcpRegistryIds)
                      .filter(
                        (entry) =>
                          entry.registry !== availableRegistryIdentity.registry
                      )
                      .map((entry) => ({
                        registry: entry.registry,
                        identifier: entry.identifier,
                      })),
                    availableRegistryIdentity,
                  ],
                }),
            ...(availableDirectorySelection === null
              ? {}
              : { healthProReceipt: availableDirectorySelection.receipt }),
            // Where they work, as the confirmed RPPS record spells it. The
            // lookup has always returned it and the screen has always shown it;
            // this is where it stops being thrown away.
            ...(employer.trim().length > 0
              ? { employer: employer.trim() }
              : {}),
            profession: professionObj.id,
            main_specialty: specialtyObj.id,
          },
        });
        // The Practice Profiles the practitioner confirmed a slide ago. Written
        // AFTER the profile PATCH, and only when the slide was actually part of
        // the flow: the write stamps "this account has answered", which is the
        // one thing separating an empty answer from never having been asked —
        // and stamping it for someone who was never shown the screen would tell
        // the Explore gate a question had been asked that never was.
        // "Later" saves nothing, so the account stays unasked. Nothing else
        // re-reads `/me` then, and its kept reading matches only the Specialty
        // this PATCH just stored.
        if (practiceSkipped)
          void revalidate("/webapp/private/practice-profiles/me");
        if (hasPracticeStep && practiceOffer !== null && !practiceSkipped) {
          // Through the shared writer, which refreshes the cached reads of this
          // answer as part of writing it. Signup used to PUT on its own and
          // refresh nothing, so the Explore gate went on reading the
          // pre-answer `confirmedAt: null` and asked a practitioner who had
          // just answered one slide earlier.
          const saved = await savePracticeProfiles({
            profileUids: [...practiceSelection],
            // The axis answer travels with the practices. Signup used to drop
            // it here, so a practitioner who named two species at registration
            // opened their settings to a question they had already answered.
            disambiguation: practiceAnswer === null ? [] : [...practiceAnswer],
            // The words travel with them, and they are the only writer that
            // ever sends any: the settings editor reads this back and hands it
            // straight to the same PUT.
            declaration: practiceDeclaration,
          });
          // Reported only now, and only if the write landed: a confirmation rate
          // counting answers that were never stored would flatter this screen
          // against the 1.2% baseline it exists to beat. A failed save leaves the
          // account unstamped, which is what the Explore gate is for — it will
          // ask again rather than let a lost answer pass for one.
          if (saved.status === "saved") {
            // Gross additions and removals against what the agent had checked,
            // which is the same baseline the list is ordered by. The net is
            // derivable from the two; neither is derivable from the net.
            const delta = practiceSelectionDelta({
              checkedOnOpen: practiceCheckedOnOpen,
              selected: practiceSelection,
              viaSearch: practiceSearchProvenance.viaSearch,
              viaAssist: practiceSearchProvenance.viaAssist,
            });
            gaEventPracticeProfilesConfirmed({
              flow: "signup",
              catalogue_size: practiceOffer.docs.length,
              total_confirmed_profiles: practiceSelection.size,
              recognised_practice_count: practiceInferredCount,
              added_count: delta.added,
              removed_count: delta.removed,
              added_via_search_count: delta.addedViaSearch,
              added_via_assist_count: delta.addedViaAssist,
              // Whether a recap was the ARRIVAL screen. Asked with no explicit
              // answer, deliberately: the live predicate goes false on the first
              // tap, and the question here is what this practitioner MET, not
              // where they stand now.
              //
              // Counted off the intersected reading, like the screen itself
              // (`practiceHasRecap` reads `practiceRecapNames`, the evidence
              // filtered through the offer). `showsPracticeRecap` alone is the
              // weaker test — it asks only whether the evidence names anything —
              // so an agent naming a uid this catalogue dropped would report a
              // recap nobody was ever shown.
              had_recap: practiceInferredCount > 0,
              // What separates a practitioner who actively confirmed from one who
              // walked past the screen the agent had checked. Both beat today's
              // 1.2%, but they are not the same product — and when the agent got it
              // right, walking past IS the correct answer.
              changed_selection: practiceProfileUids !== null,
              // Whether the catalogue was ever opened — remembered, not read off
              // the live status. Back returns to the recap from the list, so a
              // practitioner ON the recap at finalize is not necessarily one who
              // stayed on it, and this field is the one that says whether the
              // list was worth opening.
              // Both halves, because a Specialty changed and changed back
              // clears the words while this uid survives, and the event would
              // then carry `confirmed_from: "declaration"` next to
              // `declared: false`.
              confirmed_from:
                practiceDeclaredFor === effectiveSpecialty &&
                practiceDeclaration !== null
                  ? "declaration"
                  : practiceStatus === "recap" &&
                      practiceCatalogueOpenedFor !== effectiveSpecialty
                    ? "recap"
                    : "picker",
              // Separate from the field above, which names the LAST gesture:
              // someone can declare, walk back and leave on Continue, and the
              // words are still part of what they said.
              declared: practiceDeclaration !== null,
              // What the PRACTITIONER answered — not what the registry settled on
              // their behalf, which is reported by the source below.
              answered_disambiguation: practiceDisambiguation !== null,
              disambiguation_count:
                practiceDisambiguation === null
                  ? 0
                  : practiceDisambiguation.length,
              pre_selection_source:
                practicePreSelection === null
                  ? "none"
                  : practicePreSelection.source,
            });
          }
        }
        // Force a fresh read (not invalidate) so the cache holds the
        // just-persisted profession before we navigate to the profile-gated
        // JuisciGPT page — otherwise its onboarding guard reads a stale
        // pre-PATCH user and bounces back to /signup/onboarding.
        await reloadCurrentUser();
      }
    } catch {
      if (availableDirectorySelection !== null) {
        discardRegistrySelection(null);
        updateOnboardingFlow({ finalizeStarted: false });
        setFinalizing(false);
        finalizeStartedRef.current = false;
        setRegistryState(null);
        goToStep(REGISTRY_SLIDE);
        displayToast(
          t(
            "Your registry selection could not be saved. Search again or continue manually."
          ),
          "warning"
        );
        return;
      }
      // api error already captured by onRequestError.
    }

    advanceAfterCarousel();
  }

  /** The carousel is done: complete account onboarding, or bail if already done. */
  function advanceAfterCarousel(): void {
    // Already finished once: a hard reload of /signup/onboarding re-enters here
    // (via the finalize effect or the returning-user branch), but account
    // onboarding is done — drop straight into the feed instead of re-running the
    // terminal writes. `onboardingCompleted` is the only durable signal (set in
    // `finishOnboarding`).
    if (currentUser !== null && currentUser.onboardingCompleted === true) {
      // A reload while the professional record modal was open lands here with
      // the invite still pending: the signup joined no room for a gated room,
      // and `finishOnboarding` had already saved the account as complete. The
      // invite code outlives the reload (`useSignupOnboardingState`), and
      // `joinRoom` asks for the record again.
      if (roomInviteCode !== null) {
        void joinRoom(roomInviteCode, currentUser);
        return;
      }
      redirectToStartingPage();
      return;
    }

    // Nothing is left to ask: JuisciGPT asks how they heard of Juisci.
    // An invited user is auto-joined to their room and greeted by the
    // RoomJoinedModal there (see `finishOnboarding`). Onboarding does not touch
    // preference onboarding, so every new user still walks the profiling
    // conversation the first time they ask for Explore.
    void finishOnboarding();
  }

  // Finalize: once the account is ready and the reference data is loaded, save
  // the profile and finish onboarding. Ref-guarded so it runs once — both via
  // the last question's Continue and via a reload that
  // re-seeds `isFinalizing`. The finalize body is held in a ref (latest-ref
  // pattern) so the effect depends only on the finalizing flag + data readiness,
  // never on the per-render handler identity.
  const finalizeStartedRef = useRef(false);
  const finalizeRef = useRef(handleFinalizeComplete);
  finalizeRef.current = handleFinalizeComplete;
  useEffect(() => {
    if (!isFinalizing) return;
    if (finalizeStartedRef.current) return;
    if (currentUser === null && isCurrentUserLoading) return;
    if (currentUser !== null && currentUser.onboardingCompleted === true) {
      finalizeStartedRef.current = true;
      advanceAfterCarousel();
      return;
    }
    if (
      professionsQuery.state === "error" ||
      specialtiesQuery.state === "error"
    ) {
      displayToast(t("Could not load profile choices. Try again."), "warning");
      void professionsQuery.refetch();
      void specialtiesQuery.refetch();
    } else {
      if (
        professionsQuery.state !== "success" ||
        specialtiesQuery.state !== "success"
      )
        return;
      if (
        selectedProfessionAvailable &&
        specialtiesForSelectedProfession.some(
          (specialty) => specialty.uid === effectiveSpecialty
        )
      ) {
        finalizeStartedRef.current = true;
        void (async () => {
          await accountReadyRef.current;
          await finalizeRef.current();
        })();
        return;
      }
    }
    // A restored finalization must not write a profile with a retired choice.
    // Reopen the clinical fields. A later Continue starts a fresh attempt.
    finalizeStartedRef.current = true;
    setFinalizing(false);
    updateOnboardingFlow({
      finalizeStarted: false,
      registryProfileMode: "editing",
    });
    goToStep(PROFESSION_SLIDE);
  }, [
    isFinalizing,
    currentUser,
    isCurrentUserLoading,
    professionsQuery.state,
    specialtiesQuery.state,
    selectedProfessionAvailable,
    effectiveSpecialty,
    specialtiesForSelectedProfession,
  ]);

  async function finishOnboarding(): Promise<void> {
    await terminalAction.run(async () => {
      // The authoritative user, captured from the reload below rather than the
      // hook's `currentUser`: right after a fresh-account signup the closure's
      // `currentUser` can still be null (its `/users/me` transiently 401s with
      // "No user found for uid"), and `reloadCurrentUser` updating the cache
      // does NOT refresh this closure's `currentUser`. joinRoom used to read
      // that stale null and bail an invited direct-room join to "/" (→ the B2C
      // /gpt/chat/new exit), while OAuth rooms slipped past because joinRoom
      // short-circuits on `oauthRoomConfig` before the currentUser guard.
      let reloadedUser: CurrentUser;
      try {
        await apiClient.patch("/webapp/private/users/me", {
          json: { onboardingCompleted: true },
        });
        // Make the durable write authoritative in the React Query cache before
        // Discovery renders its nudge/guards.
        const reloaded = await reloadCurrentUser();
        if (reloaded === null) {
          throw new Error(
            "Invariant: onboarding completion succeeded but current user reload failed"
          );
        }
        if (reloaded.onboardingCompleted !== true) {
          throw new Error(
            "Invariant: current user reload did not include onboarding completion"
          );
        }
        reloadedUser = reloaded;
      } catch {
        // The terminal action is retryable: preserve the flow store and stay on
        // the last step until the durable writes actually succeed. Leave the
        // finalizing state so its Continue can retry; the stored flag still
        // resumes a reload.
        setFinalizing(false);
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }

      // Clean up first-visit flags so newly-onboarded users see the
      // download-app and profile-picture prompts again.
      resetOnboardingFlow();
      resetSignupFirstVisitFlags();
      iosPlaySound(SoundEffect.LIKE);

      // Fire on the authoritative reloaded user, not the hook's `currentUser`:
      // that is a stale null right after a fresh signup, which silently dropped
      // the completion event for every newly-created account.
      //
      // ONE completion per signup. An invited signup completes inside
      // `joinRoom`, with the room it joined: the BigQuery pre-aggregations
      // count completions per room, and firing here as well counted every
      // invited member once in the public app and once in the room.
      if (roomInviteCode === null)
        gaEventCompleteOnboarding(reloadedUser.uid, null, "new");

      // The page the reader was trying to open when the app sent them to sign
      // up: a shared link, or a room page followed into the native app after
      // the install (`docs/shared-content-access/README.md`). A pending invite
      // wins over it, and `joinRoom` clears it only when the join succeeds, so
      // a failed join keeps it for the next login.
      const memorizedPath = getPostLoginRedirectPath();

      // A pending room invite is auto-joined; on success the user lands in the
      // room with the RoomJoinedModal (handled inside joinRoom). `roomInviteCode`
      // is the effective code (in-memory state OR the durable store, resolved in
      // useSignupOnboardingState), so it survives a reload
      // and drives `oauthRoomConfig` — joinRoom routes OAuth-gated rooms to SSO.
      if (roomInviteCode !== null) {
        await joinRoom(roomInviteCode, reloadedUser);
        return;
      }

      clearSignupOnboardingState();

      // They came for that page, so it wins over the default exit below.
      if (memorizedPath !== "") {
        clearPostLoginRedirectPath();
        history.replace(
          await resolvePostAuthDestination(memorizedPath, reloadedUser)
        );
        return;
      }

      // B2C only — the invited (B2B) branch returned above, so it keeps its
      // in-room RoomJoinedModal welcome untouched. A public signup exits onto
      // the JuisciGPT composer instead of the feed, whose pre-prompt starters
      // are the entry point to the product.
      history.replace("/gpt/chat/new");
    });
  }

  // The Discovery landing, for the paths that are NOT the B2C signup exit (that
  // one lands on JuisciGPT, see `finishOnboarding`): the re-entry and
  // OAuth-declined paths below. The invited room, pinned room and any memorized
  // deep-link do not divert them.
  function redirectToStartingPage(): void {
    history.replace("/discovery");
  }

  async function joinRoom(
    inviteCode: string,
    // The authoritative just-reloaded user, passed by finishOnboarding — NOT the
    // hook's `currentUser`, which is a stale null right after a fresh signup and
    // used to bail this join to "/".
    user: CurrentUser
  ): Promise<void> {
    // A room that asks for a professional record admits nobody without one:
    // the registry slide may have saved it already, and the modal asks
    // otherwise. It comes before the partner sign-in of an OAuth room, whose
    // callback joins the room. A cancel ends the signup outside the room, like
    // the B2C exit, and drops the invite so that a return does not retry it.
    // After a reload the invite preview is not loaded yet: read it, since it
    // carries the room's requirement.
    let requirement: { requireMedicalIdentifier: boolean | null };
    try {
      requirement =
        invitedRoom !== null
          ? invitedRoom
          : (
              await apiClient.get("/webapp/public/rooms/by-invite/:code", {
                param: { code: inviteCode },
              })
            ).body;
    } catch {
      gaEventCompleteOnboarding(user.uid, null, "new");
      displayToast(t("Restricted access"), "error");
      clearSignupOnboardingState();
      history.replace("/discovery");
      return;
    }
    if (needsProfessionalRecord(requirement, user)) {
      const outcome = await askForProfessionalRecord();
      if (outcome === "cancelled") {
        gaEventCompleteOnboarding(user.uid, null, "new");
        clearSignupOnboardingState();
        clearRoomInviteCodeData();
        clearPostLoginRedirectPath();
        history.replace("/gpt/chat/new");
        return;
      }
    }

    if (oauthRoomConfig !== undefined) {
      // The signup completes now, outside the room: the OAuth round trip may
      // never come back. The callback then sends no second completion for
      // this account (`oauthSignupCompletionStore`).
      gaEventCompleteOnboarding(user.uid, null, "new");
      markOAuthSignupCompleted(user.uid);
      // The invite won over the memorized page (`finishOnboarding`), and the
      // OAuth flow lands in the room, so nothing reads it after this point.
      clearPostLoginRedirectPath();
      setShowOAuthModal(true);
      return;
    }

    // Whether this signup's single completion event has gone out: the join
    // can fail before it, and the account is complete either way.
    let completionSent = false;
    try {
      const response = await apiClient.post(
        "/webapp/private/rooms/join/by-invite/:code",
        {
          param: { code: inviteCode },
          json: {
            discoveryChannel:
              onboardingRoomDiscoveryChannel === null
                ? "public app"
                : onboardingRoomDiscoveryChannel,
          },
        }
      );
      const joinedRoom = response.body;
      const roomPath = getRoomInvitePathname(
        joinedRoom.id,
        onboardingRoomPlaylistId
      );

      gaEventRoomJoin({
        roomUid: joinedRoom.id,
        userUid: user.uid,
        utmSource: onboardingRoomUtmSource,
        utmMedium: onboardingRoomUtmMedium,
        utmContent: onboardingRoomUtmContent,
        origin: "onboarding",
        roomType: joinedRoom.isPublic ? "public" : "private",
        roomName: joinedRoom.translations.en.name,
        roomCount: user.rooms.length,
        roomDiscoveryChannel:
          onboardingRoomDiscoveryChannel === null
            ? "public app"
            : onboardingRoomDiscoveryChannel,
      });
      gaEventCompleteOnboarding(user.uid, joinedRoom.id, "new");
      completionSent = true;

      // Both AWAITED now that the destination is the room itself: its pages
      // derive `hasJoinedRoom` from the rooms list and read `currentUser`,
      // so navigating on the pre-join caches paints a "Join this
      // room" CTA at the member we just welcomed.
      await revalidate("/webapp/private/rooms");
      await reloadCurrentUser();

      // The room asks nothing more: the user confirmed the objection slide,
      // and lands in the room with its welcome sheet
      // (docs/partner-objection.md, REQ-05).
      clearSignupOnboardingState();
      // The invite won over the memorized page (`finishOnboarding`).
      clearPostLoginRedirectPath();
      history.replace(roomPath);
      openRoomWelcome(
        joinedRoom.id,
        onboardingRoomDiscoveryChannel === null
          ? "public app"
          : onboardingRoomDiscoveryChannel
      );
    } catch {
      if (!completionSent) gaEventCompleteOnboarding(user.uid, null, "new");
      displayToast(t("Restricted access"), "error");
      clearSignupOnboardingState();
      history.replace("/discovery");
    }
  }

  async function handleOAuthConnect(): Promise<void> {
    setShowOAuthModal(false);

    if (oauthRoomConfig === undefined || invitedRoom === null) return;

    // Logout any existing oauth token first
    for (const r of OauthRoomsConfig) {
      r.manager.logout();
    }

    const outcome = await startRoomOAuth(oauthRoomConfig, invitedRoom.id);
    // This modal is the terminal step of onboarding, opened by `joinRoom`. A
    // reader who closes the sheet has no affordance left, so reopen it:
    // `handleOAuthLater` is their way out of the flow.
    if (outcome.kind === "abandoned") setShowOAuthModal(true);
  }

  function handleOAuthLater(): void {
    setShowOAuthModal(false);
    clearSignupOnboardingState();
    redirectToStartingPage();
  }

  // --- Step submit handlers (analytics + navigation; shared by both layouts) ---

  function handleIntroAccept(): void {
    updateOnboardingFlow({ isHealthcareProfessional: true });
    gaEventHcpGateAnswered({
      outcome: "confirmed",
      is_invited_to_room: isInvitedToRoom,
    });
    goToStep(NAME_SLIDE);
  }

  /**
   * Juisci is for healthcare professionals, so declining the gate ends the
   * signup: the practitioner lands on the welcome screen, signed out.
   *
   * A restored or SSO run is signed in, and `useLogOutUser` owns that door. A
   * fresh email signup is not signed in yet (the account is created on the name
   * slide), so it drops the flow and the proved code itself.
   */
  function handleIntroDeclineHcp(): void {
    gaEventHcpGateAnswered({
      outcome: "declined",
      is_invited_to_room: isInvitedToRoom,
    });
    if (isAuthenticated) {
      void logOutUser();
      return;
    }
    resetOnboardingFlow();
    setOtpCredential(null);
    history.replace("/welcome");
  }

  function handleNameNext(): void {
    if (accountCreationAction.state.status === "running") return;
    const params = new URLSearchParams(location.search);
    if (params.get("returnTo") === "author-search") {
      gaEventNamesSubmitted();
      // Remove the returnTo param and go straight to final submit
      params.delete("returnTo");
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
      handleFinalSubmit();
      return;
    }

    if (accountCreated) {
      gaEventNamesSubmitted();
      // The country rode along on this screen, so submitting the name submits
      // it too and the flow goes wherever the country sends it.
      if (groupsQuestions) {
        leaveCountry();
        return;
      }
      goToStep(COUNTRY_SLIDE);
      return;
    }

    if (otpCredential === null) {
      gaEventNamesSubmitted();
      // No account and no proof of the address: the only way here is a reload
      // that dropped the in-memory credential between the code and the name.
      // Back to the code slide for a fresh one — advancing would walk the user
      // through the rest of the flow with nothing to create an account with at
      // the end of it.
      goToStep(OTP_SLIDE);
      return;
    }

    /**
     * The first moment the account CAN exist: the address was proven two slides
     * ago and the name has just been given. The order below is load-bearing and
     * reads bottom-up:
     *
     *   1. the account row is created for the uid the code minted,
     *   2. the flow store records that it happened,
     *   3. and only then is the browser signed in.
     *
     * Step 2 precedes step 3 for the reload or the killed app that can follow
     * the sign-in: the code's proof lives in memory and goes with them, so the
     * remounted flow reads only the store. Without step 2 it has never heard of
     * the code and restarts on the code slide.
     * Step 1 must precede step 3 for a harder reason: every private route
     * resolves the caller by uid, so a session held for a uid with no row is a
     * 401 on every private request, a session that nothing can use.
     */
    void accountCreationAction.run(async () => {
      gaEventNamesSubmitted();
      const created = await createAccountForVerifiedEmail(
        otpCredential.uid,
        {
          // The address the CODE proved, not whatever the flow store holds: on
          // desktop the two are written by different screens, and creating the
          // account for an address the proof does not cover is how a session
          // ends up refused by every private request.
          email: otpCredential.email,
          firstname: firstName.trim(),
          lastname: lastName.trim(),
          // The country slide comes after this one. The finalize PATCH fills it
          // in, as it already does for every SSO signup.
          country: null,
          language: activeLang,
          invitationCode: roomInviteCode,
          // The channel the invite join sends for the same invite.
          invitationChannel:
            roomInviteCode === null
              ? null
              : onboardingRoomDiscoveryChannel === null
                ? "public app"
                : onboardingRoomDiscoveryChannel,
        },
        // The email path is only reachable through the signup surfaces.
        "signup_page"
      );
      if (!created) return;

      updateOnboardingFlow({
        accountCreated: true,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      setCreatedEmail(otpCredential.email);
      // The flow moves on itself rather than leave it to a resume: this
      // component stays mounted through the sign-in (`SignupOnboardingRoute`).
      // On the grouped form the country was answered on this same screen, so
      // not advancing here left the practitioner looking at a screen they had
      // just submitted, pressing Continue a second time. `leaveCountry` is the
      // transition that screen owes. A reload after the sign-in resumes on the
      // registry slide, and the effect above enters the lookup again.
      if (groupsQuestions) {
        leaveCountry();
      } else {
        goToStep(COUNTRY_SLIDE);
      }
      await signInWithMintedToken(otpCredential.customToken, otpCredential.uid);
    });
  }

  /**
   * The code was entered. This proves the ADDRESS and nothing else — the account
   * cannot be created yet, because it is created with a name and the name slide
   * is still two slides away (the API requires one, and rightly).
   *
   * So the custom token is held in memory and spent by `handleNameNext`, which
   * is the first moment an account can exist. Until then nothing has been
   * created and nobody is signed in.
   */
  function handleOtpSubmit(submitted: string): void {
    void (async () => {
      const result = await otp.verifyCode(signupEmail, submitted);

      if (result === null) {
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      if (result.outcome === "rejected") {
        // The screen states the reason; the digits go so the field is ready for
        // the next attempt rather than holding a code known to be wrong.
        setOtpCode("");
        return;
      }

      acceptOtpCredential(signupEmail, result);
    })();
  }

  /**
   * Cross the code slide with a proved address. Shared by the code path and the
   * trusted-device one: what the address was proved WITH changes nothing about
   * where the carousel goes next, or about the credential onboarding carries to
   * its name screen.
   */
  function acceptOtpCredential(
    provedEmail: string,
    credential: EmailOtpCredential
  ): void {
    setOtpCredential({
      customToken: credential.customToken,
      uid: credential.uid,
      email: provedEmail,
    });
    updateOnboardingFlow({ otpVerified: true, email: provedEmail });
    setOtpCode("");
    goToStep(INTRO_SLIDE);
  }

  function handleOtpResend(): void {
    void otp.resendCode(email);
  }

  /**
   * Leave the country behind. Its own slide submits this, and so does the
   * merged name screen, which is why it is not inlined in either: the RPPS
   * lookup has to be entered the same way from both, or the desktop reaches
   * the registry slide with no request in flight.
   */
  function changeCountry(value: string): void {
    if (value === country) return;
    discardRegistrySelection(null);
    setRegistryState(null);
    setRegistryId("");
    setRegistryInitials("");
    setRepsFirstName("");
    setRepsFirstSurname("");
    setRepsSecondSurname("");
    updateOnboardingFlow({ country: value });
  }

  function leaveCountry(): void {
    if (!(country in getCountriesList())) return;
    gaEventCountrySubmitted();
    // Supported countries use their registry before the manual profession step.
    if (visitsRegistry) {
      enterRegistryLookup({ mode: "automatic" });
    } else {
      goToStep(PROFESSION_SLIDE);
    }
  }

  function handleCountrySubmit(): void {
    leaveCountry();
  }

  function handleProfessionNext(): void {
    gaEventProfessionSubmitted();
    // Single-specialty profession: `effectiveSpecialty` already holds it, so
    // submit it and skip the step rather than show a one-item dead-end list.
    // The grouped form is the same case for a different reason — the specialty
    // was answered on this screen.
    if (!hasSpecialtyStep || groupsQuestions) {
      handleSpecialtySubmit();
      return;
    }
    goToStep(SPECIALTY_SLIDE);
  }

  function selectProfession(uid: string): void {
    if (availableDirectorySelection === null) {
      updateOnboardingFlow({ profession: uid, registryProfession: "" });
      return;
    }
    const profession = professions.find((item) => item.uid === uid);
    if (profession === undefined)
      throw new Error(`Unknown selected profession ${uid}`);
    const selectedTag = specialties.find(
      (item) => item.uid === selectedSpecialty
    );
    const keepsSpecialty =
      selectedSpecialty.length === 0 ||
      profession.medical_specialties.length === 0 ||
      (selectedTag !== undefined &&
        profession.medical_specialties.includes(selectedTag.id));
    updateOnboardingFlow({
      profession: uid,
      registryProfession: "",
      specialty: keepsSpecialty ? selectedSpecialty : "",
    });
  }

  function selectSpecialty(uid: string): void {
    updateOnboardingFlow({ specialty: uid, registrySpecialty: "" });
  }

  function handleSpecialtySubmit(): void {
    gaEventMainSpecialtySubmitted();
    // Answering the Specialty on a screen the practitioner is standing on makes
    // this offer WARM, whatever the last one was. The cold marker is keyed by
    // Specialty so a different answer gets its own full wait; re-answering the
    // SAME one by hand is the case the key cannot catch, and a marker left over
    // from the fast track would hand them the 20 s ceiling and then report the
    // abandonment as watched on the practice slide.
    updateOnboardingFlow({ practiceOfferStartedColdFor: "" });
    // The Specialty's catalogue is what decides whether there is anything to
    // confirm. For 93 of the ~95 there is not, and those practitioners go
    // straight on rather than through an empty screen.
    if (hasPracticeStep)
      lookUpPractice({
        specialtyUid: effectiveSpecialty,
        // Absent, not blank. A practitioner who answered "this isn't me", or who
        // never saw the RPPS slide at all, HAS no registry identifier — and the
        // endpoint reads that as "do not join the registry" rather than as a
        // lookup of the empty string.
        registryIdentity: availableRegistryIdentity,
      });
    if (hasPracticeStep) {
      goToStep(PRACTICE_SLIDE);
      return;
    }
    leavePractice();
  }

  /**
   * Ask what is already known about this practitioner (practice-profiles ticket
   * 09) — their registry identifier first, then their public professional
   * record — so the screen arrives with their own practices checked, without
   * asking them anything extra.
   *
   * This is now the slide's DRIVER rather than a background refinement: the agent
   * reads the professional web, which takes about ten seconds, and nothing is
   * shown until it answers. What it recognises arrives PRE-CHECKED and pinned to
   * the top of the list, for the practitioner to confirm — never applied behind
   * their back. An abstention, an error or a timeout checks nothing: the
   * catalogue describes a Specialty, and only evidence about THIS practitioner is
   * allowed to check a row on their behalf. The slide then leaves, and the
   * question waits for Explore (`practiceRecognisedNothing`).
   *
   * A failure is still not an error state, and still gets no toast: there is
   * nothing to tell someone about a lookup they never asked for, and it resolves
   * to the same exit as an abstention.
   *
   * Re-asked whenever the Specialty is submitted, and BOTH answers are cleared
   * first — the evidence and the held one. Stepping back and picking another
   * Specialty must not leave the previous one's answer checked over a catalogue it
   * no longer belongs to, and re-submitting the SAME Specialty must not leave a
   * stale answer that the new reading cannot get past.
   */
  function lookUpPractice(args: {
    // Passed rather than read from the store: the fast track calls this in the
    // same turn as the `updateOnboardingFlow` that sets the Specialty, and this
    // closure would still see the previous render's value — an empty string, and
    // an inference about nobody.
    readonly specialtyUid: string;
    /** Null when there is none — the registry is optional, and "" is not an id. */
    readonly registryIdentity: typeof availableRegistryIdentity;
  }): void {
    setPracticePreSelection(null);
    setPracticeInference({ specialty: args.specialtyUid, status: "inferring" });
    // FROM SCRATCH. A held answer wins over the evidence by design — `explicit`
    // is what the practitioner tapped, and a recognition must never overwrite a
    // tap — so the answer this lookup is about to replace has to go before it
    // lands, or the whole reading is silently discarded.
    //
    // It is `practice` in a SESSION store, so it outlives a reload: without this,
    // a second run through signup in the same tab keeps the first run's checks
    // and the endpoint's answer changes nothing on screen. And re-submitting the
    // Specialty is re-entering this question, which is the moment an answer to it
    // stops being current — the practitioner is on the waiting screen when this
    // fires, never mid-list, so no live tap can be lost to it.
    updateOnboardingFlow({ practice: null });
    // Blank is the same as absent here, and the boundary says so once
    // (`nullableString`) rather than each caller trimming its own way.
    const [, emailDomain] = email.split("@");
    // Which Specialty this lookup is about. Stepping back, changing Specialty
    // and coming forward again leaves two answers in flight, and the slow one
    // can land last — checking uids from a catalogue the practitioner is no
    // longer looking at. The declared Specialty IS the identity of the request,
    // so it is what the answer is checked against rather than a counter.
    const askedFor = args.specialtyUid;
    const lookupGeneration = ++practiceLookupGeneration.current;
    practiceLookupRef.current = askedFor;
    void (async () => {
      try {
        const { body } = await apiClient.post(
          "/webapp/public/practice-profiles/pre-selection",
          {
            json: {
              specialtyUid: askedFor,
              registryIdentity: args.registryIdentity,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              // The domain, never the address: it is the part that says where
              // someone works, and the only part worth searching on.
              emailDomain: emailDomain === undefined ? null : emailDomain,
              // The catalogue is reviewed in eight languages; the agent reads it
              // in the one the practitioner is signing up in.
              language: activeLang,
            },
          }
        );
        // The server has stored this reading, and a practitioner who chose
        // "Later" may already be in the editor that reads it from `/me`.
        void revalidate("/webapp/private/practice-profiles/me");
        if (
          practiceLookupGeneration.current !== lookupGeneration ||
          practiceLookupRef.current !== askedFor
        )
          return;
        // What the agent recognised is a PROPOSAL, and the practitioner is the
        // one who answers. It is never written to the flow store here: the store
        // holds what THEY said, and filling it on their behalf would make the
        // screen unable to tell a recognition from a tap — which is the one
        // distinction `changed_selection` reports and the editor later reads.
        //
        // The checked rows come from the evidence itself (`selectedProfileUids`),
        // and the same evidence pins those rows to the top of the list
        // (`practiceCheckedOnOpen` → `practiceProfilesInPickerOrder`), where an
        // answer that is being read back can actually be read.
        setPracticePreSelection(body);
        setPracticeInference({ specialty: askedFor, status: "picking" });
      } catch (error) {
        // Reported, never shown. This failure resolves to the same exit as an
        // abstention, and a toast about a lookup they
        // never asked for would turn a nice-to-have into a visible malfunction.
        // But a lookup that fails for everyone would otherwise be invisible, so
        // it is reported.
        Sentry.captureException(error);
        if (
          practiceLookupGeneration.current !== lookupGeneration ||
          practiceLookupRef.current !== askedFor
        )
          return;
        setPracticeInference({ specialty: askedFor, status: "picking" });
      }
    })();
  }

  function handlePracticeToggle(uid: string): void {
    const profileUids = togglePracticeProfile(practiceSelection, uid);
    // Against the selection this tap PRODUCES, not the one it replaces: a set
    // that outlives the rows it describes is what let the bulk buttons credit
    // the field for practices it never won.
    writePracticeSearchPicks(new Set(profileUids), {
      uid,
      viaSearch: practiceSearch.trim() !== "",
      viaAssist: practiceAssist.assistedOnlyUids.has(uid),
    });
    updateOnboardingFlow({
      practice: {
        specialtyUid: effectiveSpecialty,
        profileUids,
        disambiguation:
          practiceDisambiguation === null ? null : [...practiceDisambiguation],
        declaration: practiceDeclaration,
      },
    });
  }

  /**
   * Store which practices the search field won, after a change to the selection.
   *
   * The RULE lives in `practiceSearchProvenanceAfter`, shared with the settings
   * editor: it was written twice and the two copies disagreed about a row
   * re-ticked off a string match, which inflated the one number that says
   * whether the assisted layer earns its request. This function only holds the
   * state and the Specialty it belongs to.
   *
   * Every change routes through here, the bulk setters included — that is the
   * other half of the same defect.
   */
  function writePracticeSearchPicks(
    selected: ReadonlySet<string>,
    reached: { uid: string; viaSearch: boolean; viaAssist: boolean } | null
  ): void {
    setPracticeSearchPicks({
      specialty: effectiveSpecialty,
      ...practiceSearchProvenanceAfter({
        current: practiceSearchProvenance,
        selected,
        reached,
      }),
    });
  }

  // The counter's bulk actions, which replace the practices and keep the axis
  // answer: they act on the LIST, and it is the list the practitioner is
  // disagreeing with. Dropping the sides too would send them back through a
  // question they have already answered.
  function handlePracticeSet(uids: readonly string[]): void {
    // A bulk set is not a field tick, so nothing is reached through it — and the
    // rows it drops lose their provenance, which is what stops "Uncheck all"
    // then "Check all" from crediting the field for a list-wide gesture.
    writePracticeSearchPicks(new Set(uids), null);
    updateOnboardingFlow({
      practice: {
        specialtyUid: effectiveSpecialty,
        profileUids: [...uids],
        disambiguation:
          practiceDisambiguation === null ? null : [...practiceDisambiguation],
        declaration: practiceDeclaration,
      },
    });
  }

  function handlePracticeToggleDisambiguation(value: string): void {
    if (practiceOffer === null) return;
    const answered = toggleDisambiguation({
      offer: practiceOffer,
      selected: practiceSelection,
      current: practiceAnswer,
      value,
    });
    // The axis answer only ever NARROWS the practices, so this can drop rows.
    // Same rule as everywhere else: a row the answer removed loses the
    // provenance that described it.
    writePracticeSearchPicks(new Set(answered.profileUids), null);
    updateOnboardingFlow({
      practice: {
        specialtyUid: effectiveSpecialty,
        profileUids: answered.profileUids,
        disambiguation: answered.disambiguation,
        declaration: practiceDeclaration,
      },
    });
  }

  // The practice screen's recap row names the Specialty it is describing, and on
  // this carousel that answer is a slide behind and out of sight. Tapping it goes
  // STRAIGHT there, past the axis question the back chevron walks through first:
  // someone correcting "Chirurgie" is not correcting which side of surgery they
  // are on.
  //
  // Null for a single-specialty profession, whose specialty slide was jumped over
  // on the way in. There is no question to reopen, so the label is not a button.
  const handleChangeSpecialty = !hasSpecialtyStep
    ? null
    : (): void => {
        gaEventSignupStepBack({
          step: resolveStepName(step),
          signup_flow: CAROUSEL_STEP_NAMES,
          is_invited_to_room: isInvitedToRoom,
        });
        goToStep(SPECIALTY_SLIDE);
      };

  // "Keep this terminology": the practitioner's own description of a practice the
  // catalogue does not carry. It WRITES AND LEAVES, in one gesture, because it is
  // an answer to the slide and not an item added to a list — there is no state of
  // this screen in which a second declaration could be typed, and a row that only
  // staged something would need a Continue the practitioner has no reason to
  // press.
  //
  // The practices are left exactly as they are: someone who checked two rows and
  // then named a third in their own words has said all three things.
  // The words arrive already trimmed, from the row that printed them: one trim,
  // in the one place that shows the practitioner what is about to go.
  function handlePracticeDeclare(declared: string): void {
    setPracticeDeclaredFor(effectiveSpecialty);
    updateOnboardingFlow({
      practiceSkippedFor: null,
      practice: {
        specialtyUid: effectiveSpecialty,
        profileUids:
          practiceProfileUids === null ? null : [...practiceProfileUids],
        disambiguation:
          practiceDisambiguation === null ? null : [...practiceDisambiguation],
        declaration: declared,
      },
    });
    // Straight past the two branches of `handlePracticeSubmit`: the field only
    // exists on the list (`showPracticeSearch`), so neither the recap nor the
    // axis question can be what this leaves.
    leavePractice();
  }

  function handlePracticeSubmit(): void {
    // The recap accepts the agent's answer and LEAVES. Before the axis rule
    // below, because the phase it would read is the one the list would open on,
    // and a recap over a Specialty that cuts its catalogue would open that list
    // instead of advancing. The axis stays unanswered on purpose: the recap
    // asked nothing, so nothing here may record an answer to it.
    if (practiceStatus === "recap") {
      updateOnboardingFlow({ practiceSkippedFor: null });
      leavePractice();
      return;
    }
    // The slide holds two questions, so its Continue walks the first one into
    // the second before it leaves. This moved here when the axis went
    // multi-select: a tap used to answer and advance in one gesture, which can
    // only ever collect one answer. `canSubmitPracticeProfiles` has already
    // established that at least one side is named.
    if (practicePhase === "axis") {
      setPracticeCatalogueOpenedFor(effectiveSpecialty);
      setPracticePhase("profiles");
      return;
    }
    // No analytics here: a confirmation counts only an answer that was saved,
    // which happens at finalize (`handleFinalizeComplete`).
    updateOnboardingFlow({ practiceSkippedFor: null });
    leavePractice();
  }

  // "Later": the question is put off and nothing is saved, so the app asks again
  // before Explore. The slide keeps its state, and Back finds it as it was.
  function handlePracticeSkip(): void {
    deferPracticeQuestion(
      // No offer yet is the wait too: the slide shows the same screen.
      practiceStatus === "inferring" || !hasPracticeStep
        ? "inferring"
        : practiceStatus === "recap"
          ? "recap"
          : practicePhase === "axis"
            ? "axis"
            : "picker"
    );
  }

  // Saves nothing: finalize sends no PUT, so `confirmedAt` stays null and the
  // Explore gate asks.
  function deferPracticeQuestion(
    skippedFrom: Parameters<
      typeof gaEventPracticeQuestionSkipped
    >[0]["skipped_from"]
  ): void {
    if (
      claimOncePerSession(
        `practice-question-skipped:signup:${effectiveSpecialty}`
      )
    ) {
      gaEventPracticeQuestionSkipped({
        flow: "signup",
        specialty_uid: effectiveSpecialty,
        skipped_from: skippedFrom,
        recognised_practice_count: practiceInferredCount,
        catalogue_size:
          practiceOffer === null ? null : practiceOffer.docs.length,
        pre_selection_source:
          practicePreSelection === null ? "none" : practicePreSelection.source,
      });
    }
    updateOnboardingFlow({ practiceSkippedFor: effectiveSpecialty });
    leavePractice();
  }

  // Past the Practice Profiles question: the objection slide.
  function leavePractice(): void {
    goToStep(PARTNER_OBJECTION_SLIDE);
  }

  // The objection screen at signup (O1), the last step. The account exists by
  // now, so the choice is saved on Continue, then the account is finalized in
  // place. A failed save keeps the slide open (spec, "Operations"). The API
  // writes one global row and informs the user. A choice already saved (a
  // finalize that failed after it) is not saved twice.
  async function handlePartnerObjectionSubmit(): Promise<void> {
    await partnerObjectionAction.run(async () => {
      const objected = flow.partnerObjected;
      const savedStage = objected ? "saved_objected" : "saved_sharing";
      // The API already holds this choice: a finalize that failed is retried
      // without a second row.
      if (flow.partnerObjectionStage === savedStage) {
        handleFinalSubmit();
        return;
      }
      try {
        await apiClient.post("/webapp/private/users/me/partner-objection", {
          json: { objected, surface: "signup" },
        });
      } catch {
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      gaEventPartnerObjectionAnswered({
        surface: "signup",
        objected,
        organisationId: null,
      });
      updateOnboardingFlow({ partnerObjectionStage: savedStage });
      handleFinalSubmit();
    });
  }

  function handleRegistrySelectCandidate(
    candidate: RegistryCandidateView
  ): void {
    if (candidate.registry === "reps" && !repsResultsCurrent) return;
    registryFromMultipleRef.current = true;
    setRegistryState({ status: "found", profile: candidate });
  }

  function handleRegistryManualExit(
    via: "out_of_scope" | "manual" | "rejected"
  ): void {
    if (registry === null)
      throw new Error("Manual registry exit requires a registry");
    discardRegistrySelection(null);
    registryAutoLookupRef.current = false;
    setRegistryState(null);
    setRegistryId("");
    setRegistryInitials("");
    setRepsSubmittedQuery(null);
    gaEventRegistryStepResolvedManually({ registry, via });
    goToStep(PROFESSION_SLIDE);
  }

  // The line announces the practice screen, so it only stands while that screen
  // is actually coming: ~93 of the ~95 specialties have no catalogue and skip
  // it, and before any specialty is picked the offer has not been asked for.
  const promisesPracticeStep = offersPractice && hasPracticeStep;

  // Title/subtitle shown per carousel form step. Moved out of the individual
  // steps so the layout owns the chrome and the step content is chrome-free.
  // Literal t() calls keep the i18n scanner able to extract the keys.
  // The desktop details screen (Figma "D7.identite.default"): the name and the
  // country, and, once the registry has answered, the profile it confirmed. The
  // subtitle says what the answers are for, which is the lookup in the country's
  // registry when there is one.
  const desktopDetailsHeader = {
    title: t("Your details"),
    subtitle:
      visitsRegistry && registry !== null
        ? t("We use them to check your {{registry}} registration.", {
            registry: registryPresentation[registry].sourceName,
          })
        : null,
  };
  const stepHeader: { title: string; subtitle: string | null } | null = (() => {
    if (typeof step !== "number") return null;
    switch (step) {
      case NAME_SLIDE:
        // The screen asks more than the name where the country rides on it, so
        // it is not called "What's your name?" there.
        return groupsQuestions
          ? desktopDetailsHeader
          : {
              title: t("What's your name?"),
              subtitle: t(
                "This takes about 2 minutes. We'll personalize your feed based on your answers."
              ),
            };
      case OTP_SLIDE:
        // Title AND subtitle in the band, like every other slide. The address
        // belongs in the subtitle: printing it in the slide body gave this one
        // screen a second heading, in a second style, under the band's own.
        return {
          title: t("Enter your code"),
          subtitle: t(
            "We sent a 6-digit code to {{email}}. No password needed.",
            { email }
          ),
        };
      case COUNTRY_SLIDE:
        return { title: t("Where do you practice?"), subtitle: null };
      case REGISTRY_SLIDE:
        // Registry slide — the header reflects the lookup state.
        if (registryState === null || registryState.status === "loading") {
          // Named, as the design has it: the practitioner is watching a
          // registry be searched for THEM, and the name they just typed is the
          // proof that is what is happening. Falls back to the generic line
          // before a name exists, which the mobile carousel can still reach.
          const searchedFor =
            registry === "reps" && repsSubmittedQuery !== null
              ? [
                  repsSubmittedQuery.firstName,
                  repsSubmittedQuery.firstSurname,
                  repsSubmittedQuery.secondSurname,
                ]
                  .filter((part) => part.length > 0)
                  .join(" ")
              : `${firstName.trim()} ${lastName.trim()}`.trim();
          if (registry === "rpps")
            return {
              title: groupsQuestions
                ? t("Verifying your profile")
                : t("Searching for your professional record"),
              subtitle:
                searchedFor === ""
                  ? t(
                      "Searching the RPPS (French Health Registry) for your credentials..."
                    )
                  : t(
                      "Searching the RPPS (French Health Registry) for {{name}}...",
                      { name: searchedFor }
                    ),
            };
          if (registry === "stps")
            return {
              title: t("Searching STPS"),
              subtitle:
                stpsSearchMode === "identifier"
                  ? t("Looking up Autorisations-ID {{identifier}} in STPS...", {
                      identifier: registryId.trim(),
                    })
                  : t("Searching STPS for {{name}}...", {
                      name: stpsName.trim(),
                    }),
            };
          return {
            title: groupsQuestions
              ? t("Verifying your profile")
              : t("Searching for your professional record"),
            subtitle:
              searchedFor === ""
                ? t("Searching {{registry}} for your credentials...", {
                    registry:
                      registry === null
                        ? ""
                        : registryPresentation[registry].sourceName,
                  })
                : t("Searching {{registry}} for {{name}}...", {
                    name: searchedFor,
                    registry:
                      registry === null
                        ? ""
                        : registryPresentation[registry].sourceName,
                  }),
          };
        }
        if (registryState.status === "inactive")
          return {
            title: t("This authorization is inactive"),
            subtitle:
              registry === "stps"
                ? t("Search STPS again or fill in your profile manually.")
                : t("Search for another authorization or continue manually."),
          };
        if (registryState.status === "unavailable")
          return {
            title: t("The registry is unavailable"),
            subtitle:
              registry === "reps"
                ? t("You can continue manually.")
                : registry === "stps"
                  ? t("Try STPS again or fill in your profile manually.")
                  : t("You can enter your registry ID or continue manually."),
          };
        // Desktop shows the confirmed profile on the details screen itself
        // (Figma "D7.identite.pays-selectionne").
        if (registryState.status === "found" && groupsQuestions)
          return desktopDetailsHeader;
        if (registryState.status === "found")
          return {
            title: t("Is this you?"),
            subtitle: t(
              "Confirm your profile to autofill your profession and specialty."
            ),
          };
        if (registryState.status === "multiple")
          return {
            title: t("We found a few matches"),
            subtitle: t("Select your profile from the list below."),
          };
        if (registryState.status === "page")
          return {
            title: t("We found a few matches"),
            subtitle: t("Select your profile from the list below."),
          };
        if (registryState.status === "editing") {
          if (registry === "stps")
            return {
              title: t("Search STPS"),
              subtitle:
                stpsSearchMode === "name"
                  ? t(
                      "Search by name and profession, or switch to Autorisations-ID."
                    )
                  : t("Enter your Autorisations-ID to search STPS."),
            };
          if (registry === "reps")
            return {
              title: t("Search REPS"),
              subtitle: t("Check your first name and both surnames in REPS."),
            };
          if (registry === "big")
            return {
              title: t("Search BIG-register again"),
              subtitle: t(
                "Check your registered initials and search again, or use your BIG number."
              ),
            };
          throw new Error("Invariant: unsupported directory search edit");
        }
        if (registryState.status === "too_many_results")
          return {
            title: t("Too many matching records"),
            subtitle:
              registry === "stps"
                ? t(
                    "Refine your name or profession, or search by Autorisations-ID."
                  )
                : registry === "big"
                  ? t(
                      "BIG returned no list. Enter your registered initials to narrow the search, or use your BIG number."
                    )
                  : t(
                      "The registry returned no list. Enter your registry number or continue manually."
                    ),
          };
        // Desktop names the registry and the person it was searched for
        // (Figma "D7.identite.rpps-introuvable"), for the registries that are
        // searched by name and then by number.
        if (
          groupsQuestions &&
          registry !== null &&
          registry !== "reps" &&
          registry !== "stps" &&
          registry !== "big"
        ) {
          const searchedFor = `${firstName.trim()} ${lastName.trim()}`.trim();
          return registry === "rpps"
            ? {
                title: t("We couldn't find you in the RPPS"),
                subtitle: t(
                  "No entry in the RPPS (French Health Registry) matches {{name}}. Enter your RPPS number, or check how your name is spelled.",
                  { name: searchedFor }
                ),
              }
            : {
                title: t("We couldn't find you in {{registry}}", {
                  registry: registryPresentation[registry].sourceName,
                }),
                subtitle: t(
                  "No entry in {{registry}} matches {{name}}. Enter your registry number, or check how your name is spelled.",
                  {
                    registry: registryPresentation[registry].sourceName,
                    name: searchedFor,
                  }
                ),
              };
        }
        return {
          title: t("We couldn't find your record"),
          subtitle:
            registry === "reps"
              ? t(
                  "Check your surnames and search REPS again, or continue manually."
                )
              : registry === "stps"
                ? t("Check the STPS name or Autorisations-ID and search again.")
                : registry === "big"
                  ? t(
                      "Check your registered initials and search again, or use your BIG number."
                    )
                  : registry === "rpps"
                    ? t(
                        "We searched the RPPS but couldn't find a matching record. You can enter your registry ID below or skip this step."
                      )
                    : t(
                        "We searched {{registry}} but couldn't find a matching record. You can enter your registry ID below or skip this step.",
                        {
                          registry:
                            registry === null
                              ? ""
                              : HCP_REGISTRIES[registry].label,
                        }
                      ),
        };
      case PROFESSION_SLIDE:
        // The screen asks more than the profession where the specialty rides
        // on it, so it is not called "What's your profession?" there. It
        // borrows the specialty slide's subtitle, because it is the specialty
        // that earns it and the two are now one screen.
        return groupsQuestions
          ? {
              title: t("Build your clinical profile"),
              subtitle: promisesPracticeStep
                ? t(
                    "It determines the specialties that will be offered to you next."
                  )
                : null,
            }
          : { title: t("What's your profession?"), subtitle: null };
      case SPECIALTY_SLIDE:
        return {
          title: t("What's your main specialty?"),
          subtitle: promisesPracticeStep
            ? t(
                "It determines the specialties that will be offered to you next."
              )
            : null,
        };
      case PRACTICE_SLIDE:
        // ONE title while the agent reads, whichever question follows it: the
        // carousel transition is still running when the wait begins, and a
        // header changing shape under it reads as a second, wrong movement.
        if (practiceStatus === "inferring")
          return { title: t("What do you practise?"), subtitle: null };
        // The recap states its own result, and it states it as a SENTENCE. The
        // band is where this carousel prints a screen's sentence at display
        // size, and the rows below are the answer it introduces. A count there
        // ("2 practices identified") reads as a system report on a screen whose
        // whole job is to sound like recognition — and the practitioner can
        // count two rows.
        if (practiceStatus === "recap")
          return {
            // The desktop design words it as the list it introduces (Figma
            // "D7.pratiques.recap").
            title: groupsQuestions
              ? t("Practices we found for you")
              : t("We recognised your practice"),
            subtitle: null,
          };
        // Then the header carries the question the screen is actually on. The
        // axis question is written by the catalogue, per Specialty, so it is the
        // only title that can be right for all of them ("Which patients do you
        // operate on?", "Which animals do you work with?") — and the design puts
        // it in this band rather than above the list (Figma node 6681:3600).
        // The picker prints neither on this surface.
        if (
          practicePhase === "axis" &&
          practiceOffer !== null &&
          practiceOffer.disambiguation !== null
        )
          return {
            title: getItemTranslation({
              translations: practiceOffer.disambiguation.question,
            }),
            subtitle: null,
          };
        return { title: t("Confirm your practice areas"), subtitle: null };
      // The slide carries its own title, under its hero icon (Figma O1).
      case PARTNER_OBJECTION_SLIDE:
        return null;
      default:
        return null;
    }
  })();

  const registryPrimaryAction:
    | { kind: "loading"; registry: typeof registry }
    | { kind: "resolving" }
    | { kind: "confirm"; profile: RegistryCandidateView }
    | {
        kind: "search";
        registry: "stps" | "reps" | "big";
        disabled: boolean;
      }
    | { kind: "identifier"; registry: typeof registry; disabled: boolean }
    | { kind: "manual"; via: "manual" | "rejected" }
    | null = (() => {
    if (step !== REGISTRY_SLIDE || registryState === null) return null;
    if (pendingRegistryConfirmation !== null) return { kind: "resolving" };
    if (registryState.status === "loading")
      return { kind: "loading", registry };
    if (registryState.status === "found")
      return { kind: "confirm", profile: registryState.profile };
    if (registry === "stps") {
      if (stpsSearchMode === "identifier")
        return {
          kind: "identifier",
          registry,
          disabled: !isWellFormedRegistryIdentifier(
            normalizeRegistryIdentifier({ registry, identifier: registryId })
          ),
        };
      return {
        kind: "search",
        registry,
        disabled: stpsName.trim().length < 3,
      };
    }
    if (registry === "reps")
      return {
        kind: "search",
        registry,
        disabled:
          repsFirstSurname.trim().length < 2 ||
          repsFirstName.trim().length > 150 ||
          repsFirstSurname.trim().length > 150 ||
          repsSecondSurname.trim().length > 150,
      };
    if (registry === "big") {
      if (registryId.trim().length > 0)
        return {
          kind: "identifier",
          registry,
          disabled: !isWellFormedRegistryIdentifier(
            normalizeRegistryIdentifier({ registry, identifier: registryId })
          ),
        };
      return {
        kind: "search",
        registry,
        disabled: !BIG_INITIALS_PATTERN.test(registryInitials.trim()),
      };
    }
    if (
      registryId.trim().length > 0 &&
      (registryState.status !== "multiple" || registryState.truncated)
    )
      return { kind: "identifier", registry, disabled: false };
    return {
      kind: "manual",
      via:
        registryState.status === "multiple" && !registryState.truncated
          ? "rejected"
          : "manual",
    };
  })();

  // Every carousel step but the intro keeps the footer CTA band. Registry
  // lookup shows its current action there, including a disabled loading state.
  // The band's measured height pads every slide of the carousel, so retaining
  // it while a request runs avoids a layout shift.
  const showStepFooter = step !== INTRO_SLIDE;

  // The practice slide's search field sits in that band too, with the country,
  // profession and specialty ones. Derived here rather than in the footer
  // because the band cannot see which of the slide's TWO questions is on
  // screen: the axis question is a handful of sides and has nothing to search,
  // and the waiting screen has no list at all yet.
  const showPracticeSearch =
    step === PRACTICE_SLIDE &&
    hasPracticeStep &&
    practiceStatus === "picking" &&
    practiceOffer !== null &&
    practicePhase === "profiles";

  // The mirror of it, for the axis question: the reassurance the design prints
  // over that Continue and nowhere else (Figma node 6681:3600). Same reason it
  // is derived here — the band cannot see which question is up.
  const showPracticeReversibleNote =
    step === PRACTICE_SLIDE &&
    hasPracticeStep &&
    practiceStatus === "picking" &&
    practiceOffer !== null &&
    practicePhase === "axis";

  // Id of the <form> the shared footer Continue submits, per slide. Named rather
  // than derived from the step index: the id used to be `onboarding-step-${step}`
  // matched against literals written inside the step components, so renumbering
  // the slides silently detached every submit button.
  const stepFormId = ((): string | null => {
    switch (step) {
      case NAME_SLIDE:
        return "onboarding-form-name";
      case OTP_SLIDE:
        return "onboarding-form-otp";
      case COUNTRY_SLIDE:
        return "onboarding-form-country";
      case PROFESSION_SLIDE:
        return "onboarding-form-profession";
      case SPECIALTY_SLIDE:
        return "onboarding-form-specialty";
      case PRACTICE_SLIDE:
        return PRACTICE_FORM_ID;
      case PARTNER_OBJECTION_SLIDE:
        return PARTNER_OBJECTION_FORM_ID;
      default:
        return null;
    }
  })();

  const showFullScreenLoader = isLoading;

  return {
    // Status
    showFullScreenLoader,
    step,
    isFinalizing,
    accountCreationState: accountCreationAction.state,
    isSavingPartnerObjection: partnerObjectionAction.state.status === "running",
    partnerObjected: flow.partnerObjected,
    handlePartnerObjectionSubmit,
    accountCreated,
    // Chrome
    stepHeader,
    // "Later" in the header, on every screen of the practice slide.
    showsPracticeSkip: step === PRACTICE_SLIDE && !isFinalizing,
    stepFormId,
    showStepFooter,
    // The desktop layout hides the back control on the gate's first slide:
    // there is nowhere behind it.
    entersFromDesktopGate,
    // Whether the country question rides on the name screen. The desktop
    // layout reads it to know whether to mount the country field there.
    groupsQuestions,
    showPracticeSearch,
    showPracticeReversibleNote,
    isCurrentStepValid,
    totalSteps,
    progressStep,
    // Flow fields
    firstName,
    lastName,
    email,
    country,
    selectedProfession,
    selectProfession,
    selectSpecialty,
    effectiveSpecialty,
    effectiveSpecialtyLabel,
    hasSpecialtyStep,
    hasProfessionStep,
    // What the registry settled, for the desktop identity card: the verified id
    // (or the one they typed on the not-found fallback), and where they work.
    registryIdentity: availableRegistryIdentity,
    registryPresentationSource,
    registryFoundSource,
    employer,
    // The card's found-record pill: a record the registry returned and the
    // practitioner confirmed, never an id typed into the not-found fallback.
    registryVerified,
    // Practice Profiles
    practiceOffer,
    hasPracticeStep,
    // Which of the slide's screens to mount: the agent reading, the recap of what
    // it found, or the picker.
    practiceStatus,
    // What the recap reads back, already translated and in catalogue order. The
    // panel caps the list itself — how many names fit is a display rule.
    practiceRecapNames,
    handlePracticeEdit,
    practiceSelection,
    practiceCheckedOnOpen,
    // The EFFECTIVE answer, not the raw one: the option a registry-settled axis
    // renders as selected is the same one that Continue was unblocked by.
    practiceDisambiguation: practiceAnswer,
    practicePhase,
    setPracticePhase,
    handleChangeSpecialty,
    practiceSearch,
    practiceAssist,
    setPracticeSearch,
    // The free-text way out, or null in the control arm. Resolved here, where
    // the flag is read, so the slide and the picker below it never learn that
    // an arm exists — they render the branch they are given.
    practiceDeclarationOffer: !offersDeclaration
      ? null
      : { query: practiceSearch, onDeclare: handlePracticeDeclare },
    // The field stops promising to FIND something once keeping the words is an
    // answer, so it is worded as the question it has become. The control arm
    // keeps the old label, where the search really is the only exit.
    practiceSearchPlaceholder: offersDeclaration
      ? t("Specify your practice")
      : t("Search practices"),
    // List searches
    countrySearch,
    setCountrySearch,
    professionSearch,
    setProfessionSearch,
    professionList,
    showMoreProfessions: () => {
      setProfessionList("expanded");
    },
    specialtySearch,
    setSpecialtySearch,
    // Registry
    registry,
    registryState,
    registryPrimaryAction,
    stpsSearch: {
      mode: stpsSearchMode,
      name: stpsName,
      professionCode: stpsProfessionCode,
      professions: stpsProfessions,
      onNameChange: changeStpsName,
      onProfessionChange: changeStpsProfessionCode,
      onModeChange: changeStpsSearchMode,
      onSearch: searchStpsAgain,
    },
    loadMoreRegistryCandidates,
    registryId,
    setRegistryId: changeRegistryId,
    registryInitials,
    setRegistryInitials: changeRegistryInitials,
    refineBigSearch,
    repsFirstName,
    setRepsFirstName: changeRepsFirstName,
    repsFirstSurname,
    setRepsFirstSurname: changeRepsFirstSurname,
    repsSecondSurname,
    repsResultsCurrent,
    setRepsSecondSurname: changeRepsSecondSurname,
    searchRepsAgain,
    // Reference data
    professions,
    specialtiesForSelectedProfession,
    specialtyRows,
    // Credentials
    emailError,
    isCheckingEmail,
    isReturningUser,
    // Email OTP
    otpCode,
    setOtpCode,
    otpState: otp.state,
    otpCooldownRemaining: otp.cooldownRemaining,
    hasSignupStarted,
    // OAuth room modal
    showOAuthModal,
    oauthRoomName: oauthRoomConfig === undefined ? null : oauthRoomConfig.name,
    roomName,
    roomLogoUrl,
    handleOAuthConnect,
    handleOAuthLater,
    // Step handlers
    handleBack,
    handleIntroAccept,
    handleIntroDeclineHcp,
    handleSignupStarted,
    handleNameNext,
    handleOtpSubmit,
    handleOtpResend,
    changeCountry,
    handleCountrySubmit,
    handleProfessionNext,
    handleSpecialtySubmit,
    handlePracticeToggle,
    handlePracticeSet,
    handlePracticeToggleDisambiguation,
    handlePracticeSubmit,
    handlePracticeSkip,
    handleRegistrySelectCandidate,
    confirmRegistryProfile,
    submitRegistryId,
    handleRegistryManualExit,
  };
}

export type OnboardingFlow = ReturnType<typeof useOnboardingFlow>;
